class SectionedBundleBox {
  constructor(apiUrl, sectionedBoxId) {
    this._apiUrl = apiUrl;
    this._sectionedBoxId = sectionedBoxId;

    this._elements = {
      container: document.querySelector(".skio-bundle-builder__container"),
      boxContainer: document.querySelector(".skio-bundle-builder__products"),
      upsellsContainer: document.querySelector(".skio-bundle-builder__upsells"),
      remainQuantityBlock: document.querySelector("[data-remain-quantity]"),
      subtotalCompareSumBlock: document.querySelector("[data-subtotal-compare-sum]"),
      subtotalMealPriceBlock: document.querySelector("[data-subtotal-meal]"),
      subtotalSumBlock: document.querySelector("[data-subtotal-sum]"),
      checkoutBlock: document.querySelector(".skio-bundle-builder__checkout-block"),
      checkoutButton: document.querySelector(".skio-bundle-builder__checkout"),
      remainQuantityContainer: document.querySelector(".skio-bundle-builder__note"),
      alertContainer: document.querySelector(".skio-bundle-builder__alert"),
      totalsContentBlock: document.querySelector(".skio-bundle-builder__totals-content"),
      progressBarContainer: document.querySelector(".skio-bundle-builder__progress-bar-container"),
      progressBarText: document.querySelector(".skio-bundle-builder__progress-bar-text"),
      progressBar: document.querySelector(".skio-bundle-builder__progress-bar"),
      paymentsBlock: document.querySelector(".skio-bundle-builder__payments"),
    };

    this._sticky = {
      container: document.querySelector(".skio-bundle-builder__sticky-bar-container"),
      progressBar: document.querySelector(".skio-bundle-builder__sticky-bar .skio-bundle-builder__progress-bar"),
      progressText: document.querySelector(".skio-bundle-builder__sticky-bar [data-progress-quantity]"),
      note: document.querySelector(".skio-bundle-builder__sticky-bar .btn-progress"),
      noteBlock: document.querySelector(".skio-bundle-builder__sticky-bar .skio-bundle-builder__note"),
      progressBarText: document.querySelector(
        ".skio-bundle-builder__sticky-bar .skio-bundle-builder__progress-bar-text",
      ),
      checkoutButton: document.querySelector(".skio-bundle-builder__sticky-bar .skio-bundle-builder__sticky-checkout"),
      quantity: document.querySelector(".skio-bundle-builder__sticky-bar [data-remain-quantity]"),
      totalsWrapper: document.querySelector(".skio-bundle-builder__sticky-bar .skio-bundle-builder__totals-content"),
      subtotal: document.querySelector(".skio-bundle-builder__sticky-bar [data-subtotal-sum]"),
      compare: document.querySelector(".skio-bundle-builder__sticky-bar [data-subtotal-compare-sum]"),
      meal: document.querySelector(".skio-bundle-builder__sticky-bar [data-subtotal-meal]"),
    };

    this._sections = [];
    this._bundleItems = [];
    this._upsellItems = [];
    this._upsellProducts = [];
    this._sellingPlanGroup = null;
    this._removeAlert = null;
    // Intervals can point at another bundle product page. Those prices come from that page's
    // own Skio sectioned box, keyed by its sectionedBoxId. Map<boxId, Promise<sections[]>>.
    this._linkedBoxes = new Map();

    this._giftVariantIds = Array.from(
      document.querySelectorAll(".skio-bundle-builder__special-offer-item[data-gift-variant-id]"),
    )
      .map((el) => el.dataset.giftVariantId.trim())
      .filter(Boolean);

    this._listeners = [];
    // Product/meal wording and serves-per-unit are section settings so one builder can be
    // re-used across product types (Bags/Meals, Boxes/Bars, etc).
    this._mealQuantity = Number(this._elements.container?.dataset.servesPerUnit) || 14;
    this._productType = this._elements.container?.dataset.productType || "Bags";
    this._mealType = this._elements.container?.dataset.mealType || "Meals";
    // Extras bundled with a subscription that inflate the compare-at price. Kept separate because a
    // double subscription buys twice the products but still only ships once.
    const productsAddedCompare = parseFloat(this._elements.container?.dataset.productsAddedCompare);
    const freeShippingCompare = parseFloat(this._elements.container?.dataset.freeShippingCompare);
    this._productsAddedCompare = Number.isFinite(productsAddedCompare) ? productsAddedCompare : 19.9;
    this._freeShippingAddedCompare = Number.isFinite(freeShippingCompare) ? freeShippingCompare : 9.95;

    // Double subscription: an extra purchase option that buys the same picks at the next size up in
    // the box's sizeInterval, on the same selling plan and at that size's percentDiscountTier.
    const sellingPlansEl = document.querySelector("[data-selling-plans]");
    this._enableDoubleSubscription = sellingPlansEl?.dataset.enableDoubleSubscription === "true";
    this._doubleSubscriptionBoxTitlePretext = sellingPlansEl?.dataset.doubleSubscriptionBoxTitlePretext || "";
    this._doubleSubscriptionText = sellingPlansEl?.dataset.doubleSubscriptionText || "";

    this._init();
  }

  async _init() {
    // Rules are storefront-wide and independent of this box, so they load alongside it.
    const [data] = await Promise.all([this._fetchData(), this._loadVolumeDiscountRules()]);
    console.log(data);
    this._addToCartText = this._elements.container?.dataset.addToCartText || "Add to Basket";
    this._oneTimeDiscountProducts = this._readOneTimeDiscountProducts();
    this._userCountryCode = this._elements.container?.dataset.userCountryCode;
    this._userCurrencyCode = this._elements.container?.dataset.userCurrencyCode;

    this._svgDelete = `
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.5 3.5L4.125 13.4999C4.15469 14.0778 4.575 14.5001 5.125 14.5001H10.875C11.4272 14.5001 11.8397 14.0778 11.875 13.4999L12.5001 3.5" stroke="black" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M2.5 3.5H13.4999" stroke="black" stroke-width="0.9375" stroke-miterlimit="10" stroke-linecap="round"/>
      <path d="M10.25 5.5L10 12.5001M8 5.5V12.5001M5.75 5.5L6.00001 12.5001M6.00001 3.5V2.25001C5.99972 2.15144 6.01892 2.05377 6.05651 1.96265C6.0941 1.87152 6.14933 1.78873 6.21903 1.71903C6.28873 1.64933 6.37153 1.59409 6.46265 1.5565C6.55378 1.51892 6.65143 1.49972 6.75 1.5H9.25001C9.34858 1.49972 9.44623 1.51892 9.53736 1.5565C9.62848 1.59409 9.71128 1.64933 9.78098 1.71903C9.85068 1.78873 9.90591 1.87152 9.9435 1.96265C9.98109 2.05377 10.0003 2.15144 10 2.25001V3.5H6.00001Z" stroke="black" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    `;

    this._svgTick = `
      <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 6.5C12 8.0913 11.3679 9.61742 10.2426 10.7426C9.11742 11.8679 7.5913 12.5 6 12.5C4.4087 12.5 2.88258 11.8679 1.75736 10.7426C0.632141 9.61742 0 8.0913 0 6.5C0 4.9087 0.632141 3.38258 1.75736 2.25736C2.88258 1.13214 4.4087 0.5 6 0.5C7.5913 0.5 9.11742 1.13214 10.2426 2.25736C11.3679 3.38258 12 4.9087 12 6.5ZM9.0225 4.2275C8.96893 4.17411 8.90514 4.13208 8.83495 4.10391C8.76476 4.07574 8.68961 4.06202 8.61399 4.06356C8.53838 4.0651 8.46385 4.08187 8.39486 4.11288C8.32588 4.14388 8.26385 4.18848 8.2125 4.244L5.60775 7.56275L4.038 5.99225C3.93137 5.89289 3.79033 5.8388 3.64461 5.84137C3.49888 5.84394 3.35984 5.90297 3.25678 6.00603C3.15372 6.10909 3.09469 6.24813 3.09212 6.39386C3.08955 6.53958 3.14364 6.68062 3.243 6.78725L5.2275 8.7725C5.28096 8.82586 5.34462 8.86791 5.41469 8.89614C5.48475 8.92437 5.55979 8.93819 5.63531 8.93679C5.71083 8.93539 5.7853 8.9188 5.85427 8.88799C5.92324 8.85719 5.9853 8.81281 6.03675 8.7575L9.03075 5.015C9.13282 4.90887 9.18921 4.76696 9.18781 4.61972C9.1864 4.47248 9.12732 4.33166 9.02325 4.2275H9.0225Z" fill="#282828"/>
      </svg>
    `;

    this._svgDropdown = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 13.3333L15.5556 7.7777" stroke="#121212" stroke-linecap="round"/>
        <path d="M10 13.3333L4.44444 7.7777" stroke="#121212" stroke-linecap="round"/>
      </svg>
    `;

    this._svgMinus = `
      <svg width="49" height="40" viewBox="0 0 49 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M31.0766 17.88V20.952H17.9246V17.88H31.0766Z" fill="#202020"/>
      </svg>
    `;

    this._svgPlus = `
      <svg width="53" height="40" viewBox="0 0 53 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M34.6621 20.984H28.1981V27.608H24.8061V20.984H18.3421V17.912H24.8061V11.288H28.1981V17.912H34.6621V20.984Z" fill="#202020"/>
      </svg>
    `;

    if (data && data.ok && data.sectionedBox) {
      const sectionedBox = data.sectionedBox;

      this._boxCurrencyCode = sectionedBox.currencyCode;
      this._defaultOneTime = !!sectionedBox.settings?.defaultOneTime;

      this._sections = this._buildSections(sectionedBox.dynamicBoxes || []);

      if (!this._sections.length) {
        console.error("Sectioned box has no dynamic boxes");
        return;
      }

      this._sizeInterval = this._computeGlobalSizeInterval();
      this._preselectedSize = this._sizeInterval[0];
      this._sellingPlanGroup = this._getSharedSellingPlanGroup();

      this._upsellProducts = this._readUpsellProducts();

      if (this._userCurrencyCode !== this._boxCurrencyCode) {
        await this._overrideWithMarketPrices(this._userCountryCode);
      }

      this._renderIntervalBlocks();
      this._renderSellingPlans(this._sellingPlanGroup);
      this._addSellingPlanPercent();
      this._setupPurchaseOptionListener();
      this._setupIntervalOptionListener();
      const intervalBlock = document.querySelector("[data-box-interval]");
      this._updateIntervalPriceBlock(intervalBlock);
      this._renderProductCards();
      this._renderProductUpsells();
      this._setupCheckoutButtonListener();
      this._updateRemainingQuantityBlock();
      this._updateMainPrice();
      this._renderProgressBarBlocks();
      this._renderStickyProgressBarBlocks();
      this._updateSectionProgressBars();
      this._updateStickyUI();
      this._updatePaymentBlock();
      this._bindStickyCheckoutButton();
      this._updateMediaBySize(false);
      this._initStickyBarVisibilityObserver();
      this._updateDiscountBlockTop();
      this._initReviewsSlider();
      this._initCartGiftWatcher();
    }
  }

  async _fetchData(sectionedBoxId = this._sectionedBoxId) {
    try {
      const res = await fetch(this._apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        body: JSON.stringify({
          sectionedBoxId,
        }),
      });

      if (!res.ok) {
        return null;
      }

      return await res.json();
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }

  // A standard (non-sectioned) Skio bundle is a single dynamic box keyed by product id, on a
  // different endpoint. Its DynamicBox has the same shape _buildSections reads, and the pricing
  // math in skio-bundle-builder-v2.js matches the single-section path here, so one box in an
  // array prices identically to how the linked page prices itself.
  async _fetchDynamicBox(productPlatformId) {
    try {
      const res = await fetch("https://api.skio.com/storefront-http/get-dynamic-box", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        body: JSON.stringify({
          productPlatformId,
        }),
      });

      if (!res.ok) {
        return null;
      }

      return await res.json();
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }

  // Skio's storefront-wide quantity ("volume") discount rules, keyed by variant. This is a separate
  // Skio feature from a box's percentDiscountTier: a section whose products carry volume rules
  // typically has its tier zeroed and takes its whole discount from here. Rules cover every box on
  // the storefront, so linked bundles are priced from the same set.
  async _loadVolumeDiscountRules() {
    this._volumeDiscountRules = [];

    const shop = window.Shopify?.shop;
    if (!shop) return;

    try {
      const res = await fetch(
        `https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window.Shopify.shop}`,
        {
          headers: {
            Accept: "*/*",
          },
        },
      );

      if (!res.ok) return;

      const data = await res.json();

      // The box prices in percentages, so only percentage rules can be represented. Anything else is
      // dropped rather than silently mispriced.
      this._volumeDiscountRules = (data?.rules || [])
        .filter((rule) => {
          if (!Array.isArray(rule?.productVariantIds) || !rule.productVariantIds.length) return false;
          if (rule.discountType !== "PERCENTAGE") {
            console.warn("Skio volume discount rule ignored, unsupported discountType:", rule.discountType);
            return false;
          }
          return true;
        })
        .map((rule) => ({ ...rule, variantGids: new Set(rule.productVariantIds) }));
    } catch (error) {
      console.error("Error loading Skio volume discount rules:", error);
    }
  }

  // Loads the bundle behind a linked interval's product page so its prices can be calculated with
  // the same math as this box. Cached per type+id, one request each. Skio prices the linked box in
  // its own currency, so it gets the same market override as this box before anything prices it.
  _loadLinkedBundle(bundleId, bundleType) {
    if (!bundleId) return Promise.resolve(null);

    const cacheKey = `${bundleType}:${bundleId}`;

    if (!this._linkedBoxes.has(cacheKey)) {
      const request =
        bundleType === "sectioned"
          ? this._fetchData(bundleId).then((data) =>
              data && data.ok && data.sectionedBox
                ? { dynamicBoxes: data.sectionedBox.dynamicBoxes || [], currencyCode: data.sectionedBox.currencyCode }
                : null,
            )
          : this._fetchDynamicBox(bundleId).then((data) =>
              data && data.DynamicBox
                ? { dynamicBoxes: [data.DynamicBox], currencyCode: data.DynamicBox.currencyCode }
                : null,
            );

      const promise = request
        .then(async (box) => {
          if (!box) return null;
          const sections = this._buildSections(box.dynamicBoxes);
          if (!sections.length) return null;
          if (this._userCurrencyCode !== box.currencyCode) {
            await this._overrideWithMarketPrices(this._userCountryCode, sections);
          }
          return sections;
        })
        .catch((error) => {
          console.error("Error loading linked bundle", cacheKey, error);
          return null;
        });

      this._linkedBoxes.set(cacheKey, promise);
    }

    return this._linkedBoxes.get(cacheKey);
  }

  // Pricing context for an interval container: null for intervals of this box, or the linked
  // bundle's sections + size + serves-per-unit once its data has resolved.
  _getLinkedContext(container) {
    if (!container?.dataset.linkedBundleId) return null;

    const sections = container._linkedSections;
    if (!sections) return null;

    const requiredSections = this._requiredSections(sections);
    const sizeInterval = (requiredSections[0] || sections[0])?.sizeInterval || [];
    const configuredSize = parseFloat(container.dataset.linkedSize);
    const size = Number.isFinite(configuredSize) && configuredSize > 0 ? configuredSize : sizeInterval[0];
    if (!size) return null;

    const servesPerUnit = parseFloat(container.dataset.linkedServesPerUnit);

    return {
      sections,
      size,
      mealQuantity: Number.isFinite(servesPerUnit) && servesPerUnit > 0 ? servesPerUnit : this._mealQuantity,
    };
  }

  _readUpsellProducts() {
    const script = document.querySelector("[data-sectioned-upsells]");
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed.filter((p) => p && p.productVariants?.length) : [];
    } catch (error) {
      console.error("Error parsing upsell products JSON:", error);
      return [];
    }
  }

  _computeGlobalSizeInterval() {
    const required = this._requiredSections();
    const base = (required[0] || this._sections[0]).sizeInterval || [];

    const shared = base.filter((size) =>
      this._sections.every((section) => !section.sizeInterval.length || section.sizeInterval.includes(size)),
    );

    return shared.length ? shared : base;
  }

  _requiredSections(sections = this._sections) {
    return sections.filter((section) => !section.optional);
  }

  _getSectionByIndex(sectionIndex) {
    return this._sections[sectionIndex] || null;
  }

  _getSectionOfVariant(variantId) {
    return (
      this._sections.find((section) =>
        this._getAllVariants(section.products).some((v) => v.platformId?.split("/").pop() === String(variantId)),
      ) || null
    );
  }

  _getSectionQuantity(sectionIndex) {
    return this._bundleItems
      .filter((item) => item.sectionIndex === sectionIndex)
      .reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
  }

  _roundMoney(value) {
    return Math.round(value * 100) / 100;
  }

  _floorMoney(value) {
    return Math.floor(value * 100) / 100;
  }

  _roundedNumber(value) {
    return Math.round(value * 100) / 100;
  }

  // Formats in the shopper's currency with its own symbol ($, £, €), not a hardcoded "$".
  _formatMoney(amount) {
    const currency = this._userCurrencyCode || this._boxCurrencyCode || "AUD";
    let formatted;
    try {
      formatted = new Intl.NumberFormat("en", {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
      }).format(Number(amount));
    } catch (error) {
      formatted = "$" + Number(amount).toFixed(2);
    }
    if (window.showCurrencyCode && this._userCurrencyCode) {
      return `${formatted} ${this._userCurrencyCode}`;
    }
    return formatted;
  }

  _getAllVariants(products) {
    if (!Array.isArray(products)) return [];
    return products.flatMap((product) => product.productVariants || []);
  }

  _getAllSectionsVariants() {
    return this._sections.flatMap((section) => this._getAllVariants(section.products));
  }

  _getCheapestVariant(allVariants) {
    if (!allVariants || allVariants.length === 0) return null;

    const cheapest = allVariants.reduce((currentCheapest, variant) => {
      return variant.price < currentCheapest.price ? variant : currentCheapest;
    }, allVariants[0]);

    return {
      price: cheapest.price,
      compareAtPrice: cheapest.compareAtPrice,
      platformId: cheapest.platformId,
      id: cheapest.id,
    };
  }

  _getSectionCheapest(section) {
    return this._getCheapestVariant(this._getAllVariants(section.products));
  }

  _getSectionCheapestAdjusted(section) {
    const sellingPlans = section.sellingPlanGroup;
    if (!sellingPlans || typeof sellingPlans !== "object") return null;
    const plans = Object.values(sellingPlans);
    if (!plans.length) return null;

    const selectableGids = new Set(
      this._getAllVariants(section.products)
        .map((v) => v.platformId)
        .filter(Boolean),
    );

    let cheapestVariant = null;

    for (const plan of plans) {
      const variantMapping = plan.variantMapping || {};

      for (const variantId in variantMapping) {
        if (!selectableGids.has(variantId)) continue;

        const variant = variantMapping[variantId];

        if (!cheapestVariant || variant.adjustedPrice < cheapestVariant.adjustedPrice) {
          cheapestVariant = {
            variantId,
            planId: variant.planId,
            adjustedPrice: this._roundedNumber(variant.adjustedPrice),
          };
        }
      }
    }

    return cheapestVariant;
  }

  // One-time quantity discounts come from the custom.bundle_one_time_discounts metafield on the
  // products nominated in the theme editor. Metafields are Liquid-only, so the section renders them
  // into the page as JSON, each entry tagged with its product id.
  _readOneTimeDiscountProducts() {
    const script = document.querySelector("[data-sectioned-one-time-discounts]");
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((entry) => ({
          productId: String(entry?.product_id ?? "").trim(),
          tiers: (Array.isArray(entry?.tiers) ? entry.tiers : [])
            .map((tier) => ({
              quantity: parseInt(tier?.item_quantity, 10),
              percent: parseFloat(tier?.discount_percentage),
            }))
            .filter((tier) => Number.isFinite(tier.quantity) && tier.quantity > 0 && Number.isFinite(tier.percent))
            // Ascending, so the lookup below can walk up to the deepest breakpoint the box reaches.
            .sort((a, b) => a.quantity - b.quantity),
        }))
        .filter((entry) => entry.productId && entry.tiers.length);
    } catch (error) {
      console.error("Error parsing one-time discount tiers JSON:", error);
      return [];
    }
  }

  // A section takes the ladder of whichever nominated product it contains. Binding by membership
  // rather than by list order matters because section order comes from Skio's dynamicBoxes and is
  // independent of the theme editor, so a reorder there must not shift ladders between boxes.
  _getSectionOneTimeTiers(section) {
    if (!this._oneTimeDiscountProducts?.length || !section) return null;
    if (section._oneTimeTiers !== undefined) return section._oneTimeTiers;

    const productIds = new Set(
      (section.products || []).map((product) => product.productPlatformId?.split("/").pop()).filter(Boolean),
    );

    const matches = this._oneTimeDiscountProducts.filter((entry) => productIds.has(entry.productId));

    if (matches.length > 1) {
      console.warn(
        `Multiple one-time discount products matched section "${section.title}", using the first.`,
        matches.map((entry) => entry.productId),
      );
    }

    section._oneTimeTiers = matches[0]?.tiers || null;

    return section._oneTimeTiers;
  }

  // item_quantity is an "at least" breakpoint, so a box takes the highest tier it reaches rather
  // than needing an exact match: with tiers at 4 and 8 configured, a 12 pack falls back to the 8.
  _getOneTimeQuantityDiscountPercent(section, size) {
    const tiers = this._getSectionOneTimeTiers(section);
    if (!tiers?.length || !(size > 0)) return 0;

    let percent = 0;
    for (const tier of tiers) {
      if (tier.quantity > size) break;
      percent = tier.percent;
    }

    return percent;
  }

  // Volume discounts land on a whole box at once: the quantity tested is the section's box size, and
  // the winning rule's percentage comes off every product in that section. A rule only counts if it
  // covers every selectable variant of the section, since a rule covering just some of them could
  // not be applied uniformly across the box.
  _getVolumeDiscountPercent(section, quantity) {
    if (!this._volumeDiscountRules?.length || !section || !(quantity > 0)) return 0;

    const cache = (section._volumeDiscountCache ||= {});
    if (cache[quantity] != null) return cache[quantity];

    if (!section._volumeDiscountGids) {
      section._volumeDiscountGids = new Set(
        this._getAllVariants(section.products)
          .map((variant) => variant.platformId)
          .filter(Boolean),
      );
    }

    const variantGids = section._volumeDiscountGids;
    let percent = 0;

    if (variantGids.size) {
      this._volumeDiscountRules.forEach((rule) => {
        const min = rule.minQuantityToDiscount;
        const max = rule.maxQuantityToDiscount;

        // A null bound is open-ended on that side.
        if (min != null && quantity < min) return;
        if (max != null && quantity > max) return;
        if (![...variantGids].every((gid) => rule.variantGids.has(gid))) return;

        // Overlapping tiers are normal (1+/2+/3+/4+); the deepest matching one wins.
        percent = Math.max(percent, parseFloat(rule.discountAmount) || 0);
      });
    }

    cache[quantity] = percent;
    return percent;
  }

  // Every price path reads its section discount here. The percentDiscountTier applies to every
  // purchase type; volume discounts are subscription-only, so they are added only when pricing a
  // subscription. Defaults to one-time, the safe direction for any caller that does not opt in.
  _getDiscountPercent(section, size, isSubscription = false) {
    const tierPercent = parseFloat(section.percentDiscountTier?.[size]) || 0;

    if (!isSubscription) {
      // The metafield ladder is the source of one-time quantity discounts for sections that have a
      // nominated product. Sections without one keep falling back to the Skio tier as before.
      if (!this._getSectionOneTimeTiers(section)?.length) return tierPercent;

      return Math.min(100, this._getOneTimeQuantityDiscountPercent(section, size));
    }

    // Tier and volume discounts are configured independently in Skio and sum, matching how the
    // selling-plan adjustment and bundle discount are already added together for the badge.
    return Math.min(100, tierPercent + this._getVolumeDiscountPercent(section, size));
  }

  // ctx is null for this box, or { sections, mealQuantity } when pricing a linked box.
  // addedCompareMultiplier is how many of the picked box `size` represents, so a double order
  // counts its free products once per box while still only shipping once.
  _getBoxPricing(size, isSubscription, ctx = null, addedCompareMultiplier = 1) {
    let finalPrice = 0;
    let comparePrice = 0;

    const mealQuantity = ctx?.mealQuantity || this._mealQuantity;
    const requiredSections = this._requiredSections(ctx?.sections || this._sections);

    requiredSections.forEach((section) => {
      const cheapest = this._getSectionCheapest(section);
      if (!cheapest) return;

      const percent = this._getDiscountPercent(section, size, isSubscription);

      let unitPrice;
      if (isSubscription) {
        const adjusted = this._getSectionCheapestAdjusted(section);
        unitPrice = this._roundMoney(adjusted?.adjustedPrice > 0 ? adjusted.adjustedPrice : cheapest.price);
      } else {
        unitPrice = this._roundMoney(cheapest.price);
      }

      const unitDiscount = this._floorMoney(unitPrice * (percent / 100));
      finalPrice += (unitPrice - unitDiscount) * size;

      const compareUnit =
        cheapest.compareAtPrice != null && cheapest.compareAtPrice > cheapest.price
          ? cheapest.compareAtPrice
          : cheapest.price;
      comparePrice += compareUnit * size;
    });

    // Returned separately so callers can tell the extras apart from the box itself.
    const giftCompareValue = isSubscription ? this._getGiftCompareValue(addedCompareMultiplier) : 0;
    comparePrice += giftCompareValue;

    const savings = Math.max(0, comparePrice - finalPrice);
    const totalUnits = size * requiredSections.length;
    const perMeal = totalUnits > 0 ? finalPrice / (totalUnits * mealQuantity) : 0;

    return { finalPrice, comparePrice, giftCompareValue, savings, perMeal, totalUnits };
  }

  // For a double order `size` is already the larger sizeInterval, so only the extras need the
  // multiplier — the free shipping value is still counted once.
  _calculateSubscriptionDiscountSum(size, ctx = null, addedCompareMultiplier = 1) {
    const oneTimeCompare = this._requiredSections(ctx?.sections || this._sections).reduce((sum, section) => {
      const cheapest = this._getSectionCheapest(section);
      return cheapest ? sum + cheapest.price * size : sum;
    }, 0);

    const normalPrice = oneTimeCompare + this._getGiftCompareValue(addedCompareMultiplier);
    const subscriptionPrice = this._getBoxPricing(size, true, ctx).finalPrice;
    const difference = Math.max(0, normalPrice - subscriptionPrice);
    const percent = normalPrice > 0 ? Math.round((difference / normalPrice) * 100) : 0;

    return { normalPrice, subscriptionPrice, difference, percent };
  }

  _getOneTimeDiscountSum(size, ctx = null) {
    return this._requiredSections(ctx?.sections || this._sections).reduce((sum, section) => {
      const cheapest = this._getSectionCheapest(section);
      if (!cheapest) return sum;
      const percent = this._getDiscountPercent(section, size);
      const unitDiscount = this._floorMoney(this._roundMoney(cheapest.price) * (percent / 100));
      return sum + unitDiscount * size;
    }, 0);
  }

  _getMaxDiscountPercent(size, isSubscription = false) {
    return this._sections.reduce(
      (max, section) => Math.max(max, this._getDiscountPercent(section, size, isSubscription)),
      0,
    );
  }

  _renderIntervalBlocks() {
    this._scaleElements = [];
    const container = document.querySelector("[data-box-interval]");
    if (!container) return;
    container.innerHTML = "";

    const intervals = JSON.parse(container.dataset.intervals);
    const requiredCount = this._requiredSections().length;

    // Cards render in theme-editor block order. Intervals of this box still have to match one of
    // the box's sizes to appear (first block wins per size); linked intervals aren't sizes of this
    // box, so they're always kept.
    const seenSizes = new Set();
    const renderList = intervals
      .map((intervalOpt) => ({ size: parseFloat(intervalOpt.size) || 0, intervalOpt }))
      .filter(({ size, intervalOpt }) => {
        if (intervalOpt.linked_bundle_id) return true;
        if (seenSizes.has(size) || !this._sizeInterval.some((boxSize) => boxSize == size)) return false;
        seenSizes.add(size);
        return true;
      });

    renderList.forEach(({ size, intervalOpt }) => {
      const isLinked = !!intervalOpt.linked_bundle_id;

      const intervalContainer = intervalOpt.product_link
        ? document.createElement("a")
        : document.createElement("label");

      intervalContainer.classList.add("skio-bundle-builder__interval-container");

      if (intervalOpt.product_link) {
        intervalContainer.href = intervalOpt.product_link;
      }

      if (intervalOpt.product_link === window.location.pathname) {
        intervalContainer.classList.add("skio-bundle-builder__interval-container--active");
        intervalContainer.style.pointerEvents = "none";
      }

      // A linked interval navigates to another product page, so it must stay out of the
      // "bundle-size" radio group that drives this box's selected size.
      let input = null;
      if (isLinked) {
        intervalContainer.dataset.linkedBundleId = intervalOpt.linked_bundle_id;
        intervalContainer.dataset.linkedBundleType = intervalOpt.linked_bundle_type || "standard";
        if (intervalOpt.linked_size) intervalContainer.dataset.linkedSize = intervalOpt.linked_size;
        if (intervalOpt.linked_serves_per_unit) {
          intervalContainer.dataset.linkedServesPerUnit = intervalOpt.linked_serves_per_unit;
        }
      } else {
        input = document.createElement("input");
        input.type = "radio";
        input.name = "bundle-size";
        input.value = size;
        input.classList.add("visually-hidden");
        if (input.value == this._preselectedSize) input.checked = true;

        intervalContainer.appendChild(input);
      }

      const topBoxValue = intervalOpt.interval_top_badge;

      const topBadgeText = document.createElement("span");
      topBadgeText.classList.add("box-badge", "box-badge--orange", "top-badge");
      topBadgeText.textContent = topBoxValue;

      const sizeText = document.createElement("span");
      sizeText.classList.add("interval-title");

      if (intervalOpt.title) {
        sizeText.textContent = intervalOpt.title;
      } else {
        sizeText.textContent = requiredCount > 1 ? `${size} ${this._productType} Each` : `${size} ${this._productType}`;
      }

      const mealText = document.createElement("span");
      mealText.classList.add("interval-text");
      if (intervalOpt.description) {
        mealText.innerHTML = intervalOpt.description;
      } else {
        mealText.textContent = `${size * requiredCount * this._mealQuantity} ${this._mealType}`;
      }

      const img = document.createElement("img");
      img.src = intervalOpt.image;
      img.alt = sizeText.textContent;

      const intervalPriceBlock = document.createElement("span");
      intervalPriceBlock.classList.add("skio-bundle-builder__price-block");

      if (isLinked) {
        // Placeholder from the linked product's Liquid price until its box data resolves; the
        // standard classes are used so _updateIntervalPriceBlock overwrites it with live pricing.
        this._renderPlaceholderPriceContainer(intervalPriceBlock, intervalOpt);
      } else {
        this._renderPriceContainer(size, intervalPriceBlock, false, false, false, intervalOpt);
      }

      const badgeText = container.getAttribute("data-badge-text");
      const intervalPriceBadge = document.createElement("span");
      intervalPriceBadge.classList.add("skio-bundle-builder__interval-badge", "box-badge", "box-badge--dark");
      if (!isLinked) {
        const pricing = this._getBoxPricing(size, this._isSellingPlanSelected());
        intervalPriceBadge.textContent = `${this._formatMoney(pricing.perMeal)} ${badgeText}`;
      }

      if (topBoxValue) {
        intervalContainer.appendChild(topBadgeText);
      }

      intervalContainer.appendChild(img);
      intervalContainer.appendChild(sizeText);
      intervalContainer.appendChild(mealText);
      intervalContainer.appendChild(intervalPriceBlock);

      if (isLinked) {
        const discountContainer = document.createElement("div");
        discountContainer.classList.add("discount-container");

        // Filled in by _updateIntervalPriceBlock once the linked box's pricing is available.
        const discountBlock = document.createElement("div");
        discountBlock.classList.add("discount-block", "box-badge");

        discountContainer.appendChild(discountBlock);
        intervalContainer.appendChild(discountContainer);
      } else if (intervalOpt.savings_percentage) {
        const discountContainer = document.createElement("div");
        discountContainer.classList.add("discount-container");

        const discountBlock = document.createElement("div");
        discountBlock.classList.add("discount-block", "box-badge");
        const { difference } = this._calculateSubscriptionDiscountSum(size);
        discountBlock.textContent = difference > 0 ? `Save ${this._formatMoney(difference)}` : "";

        discountContainer.appendChild(discountBlock);
        intervalContainer.appendChild(discountContainer);
      }

      container.appendChild(intervalContainer);
      if (input) this._scaleElements.push(input);
    });

    this._loadLinkedIntervalPricing(container);
  }

  // Fetches each linked interval's bundle and re-prices that interval when it arrives.
  _loadLinkedIntervalPricing(container) {
    const linkedContainers = container.querySelectorAll("[data-linked-bundle-id]");

    linkedContainers.forEach((linkedContainer) => {
      const { linkedBundleId, linkedBundleType } = linkedContainer.dataset;

      this._loadLinkedBundle(linkedBundleId, linkedBundleType).then((sections) => {
        if (!sections || !linkedContainer.isConnected) return;
        linkedContainer._linkedSections = sections;
        this._updateIntervalContainerPrice(linkedContainer, this._isSellingPlanSelected());
      });
    });
  }

  _renderPlaceholderPriceContainer(priceBlock, intervalOpt) {
    priceBlock.innerHTML = "";

    const price = document.createElement("span");
    price.classList.add("price-default");
    price.textContent = intervalOpt.product_price || "";

    const comparePrice = document.createElement("span");
    comparePrice.classList.add("price-compare");
    comparePrice.textContent = intervalOpt.product_compare_at_price || "";

    priceBlock.appendChild(price);
    priceBlock.appendChild(comparePrice);
  }

  _renderPriceContainer(
    size,
    priceBlock,
    showFrom = true,
    isSubscription = false,
    isOneTime = false,
    intervalOpt = null,
    isDouble = false,
  ) {
    priceBlock.innerHTML = "";

    if (isSubscription) {
      priceBlock.setAttribute(isDouble ? "data-double-subscription-price" : "data-subscription-price", "");
    }

    // For the double option `size` is already the larger sizeInterval, so the box price and its
    // discount tier both come straight from it. Only the extras need to know how many of the
    // picked box that represents.
    const pickedSize = parseInt(this._getSelectedBundleTotalSize());
    const addedCompareMultiplier = isDouble && pickedSize ? size / pickedSize : 1;

    const applyBundleDiscount = isSubscription || isOneTime;
    const pricing = applyBundleDiscount
      ? this._getBoxPricing(size, isSubscription, null, addedCompareMultiplier)
      : this._getUndiscountedBoxPricing(size);

    const finalPrice = pricing.finalPrice;

    const comparePrice = document.createElement("span");
    comparePrice.classList.add("price-compare");

    const compareValue = pricing.comparePrice;

    if (intervalOpt?.product_compare_at_price) {
      comparePrice.textContent = intervalOpt.product_compare_at_price;
      comparePrice.classList.add("product-compare-price");
    } else if (compareValue > finalPrice) {
      comparePrice.textContent = `${this._formatMoney(compareValue)}`;
      comparePrice.classList.add("product-compare-price");
    } else {
      comparePrice.textContent = "";
    }

    const price = document.createElement("span");
    price.classList.add("price-default");
    if (intervalOpt?.product_price) {
      price.textContent = intervalOpt.product_price;
      price.classList.add("product-price");
    } else {
      price.textContent = `${showFrom ? "From " : ""}${this._formatMoney(finalPrice)}`;
    }

    priceBlock.appendChild(price);
    priceBlock.appendChild(comparePrice);
  }

  _getUndiscountedBoxPricing(size, ctx = null) {
    let finalPrice = 0;
    let comparePrice = 0;

    this._requiredSections(ctx?.sections || this._sections).forEach((section) => {
      const cheapest = this._getSectionCheapest(section);
      if (!cheapest) return;

      finalPrice += cheapest.price * size;

      const compareUnit =
        cheapest.compareAtPrice != null && cheapest.compareAtPrice > cheapest.price
          ? cheapest.compareAtPrice
          : cheapest.price;
      comparePrice += compareUnit * size;
    });

    return { finalPrice, comparePrice };
  }

  async _fetchBatchMarketPrices(variantIds, countryCode) {
    const ids = variantIds.map((id) => `"${id}"`).join(", ");
    const query = `
      query batchVariantPrices @inContext(country: ${countryCode}) {
        nodes(ids: [${ids}]) {
          ... on ProductVariant {
            id
            price { amount }
            compareAtPrice { amount }
          }
        }
      }
    `;

    try {
      // Tokenless Storefront API: variant prices need no access token, and a token that is revoked
      // fails the whole request with 401 rather than falling back. Prices stay in the base currency
      // whenever this fails, so the error is logged loudly.
      const res = await fetch("/api/2026-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      });
      const data = await res.json();
      if (!res.ok || data?.errors || !data?.data?.nodes) {
        console.error("Market price query failed, prices left in base currency:", res.status, data?.errors);
        return {};
      }
      const priceMap = {};
      for (const node of data.data.nodes) {
        if (!node?.id) continue;
        priceMap[node.id] = {
          price: parseFloat(node.price.amount),
          compareAtPrice: node.compareAtPrice ? parseFloat(node.compareAtPrice.amount) : null,
        };
      }
      return priceMap;
    } catch (error) {
      console.error("Error fetching batch market prices:", error);
      return {};
    }
  }

  // Upsells belong to this box, so they are only repriced alongside this box's own sections.
  async _overrideWithMarketPrices(countryCode, sections = this._sections) {
    const boxVariants = sections.flatMap((section) => this._getAllVariants(section.products));
    const upsellVariants = sections === this._sections ? this._getAllVariants(this._upsellProducts) : [];
    const combined = [...boxVariants, ...upsellVariants];
    if (!combined.length) return;

    const variantIds = [...new Set(combined.map((v) => v.platformId).filter(Boolean))];
    if (!variantIds.length) return;

    const priceMap = await this._fetchBatchMarketPrices(variantIds, countryCode);
    if (!Object.keys(priceMap).length) return;

    for (const variant of combined) {
      const marketData = priceMap[variant.platformId];
      if (!marketData) continue;
      variant.price = marketData.price;
      // A market without a compare-at price must clear it, not keep the base-currency one.
      variant.compareAtPrice = marketData.compareAtPrice;
    }

    sections.forEach((section) => {
      const plans = Object.values(section.sellingPlanGroup || {});
      for (const plan of plans) {
        if (!plan.variantMapping) continue;
        const adjustmentPercent = plan.priceAdjustmentAmount || 0;
        for (const gid in plan.variantMapping) {
          const marketData = priceMap[gid];
          if (!marketData) continue;
          plan.variantMapping[gid].adjustedPrice = this._roundedNumber(
            (marketData.price * (100 - adjustmentPercent)) / 100,
          );
        }
      }
    });
  }

  _getMaxPriceAdjustmentAmount(sellingPlans) {
    if (!sellingPlans || typeof sellingPlans !== "object") return 0;
    let maxAdjustment = 0;

    const plansArray = Object.values(sellingPlans);
    if (!plansArray.length) return 0;

    for (const plan of plansArray) {
      if (plan.priceAdjustmentAmount > maxAdjustment) {
        maxAdjustment = plan.priceAdjustmentAmount;
      }
    }

    return maxAdjustment;
  }

  // Every section of a box subscribes on the same plans, but a section can come back with an
  // empty sellingPlanGroup (e.g. an optional one), so take the first section that actually has
  // plans instead of assuming it is section 0.
  _getSharedSellingPlanGroup() {
    const withPlans = this._sections.filter(
      (section) => section.sellingPlanGroup && Object.keys(section.sellingPlanGroup).length > 0,
    );

    if (!withPlans.length) return {};

    const shared = withPlans[0].sellingPlanGroup;
    const sharedIds = Object.keys(shared).sort().join(",");

    withPlans.slice(1).forEach((section) => {
      if (Object.keys(section.sellingPlanGroup).sort().join(",") !== sharedIds) {
        console.warn(
          `Section "${section.title}" has different selling plans to "${withPlans[0].title}"; using the latter.`,
        );
      }
    });

    return shared;
  }

  _hasSellingPlans() {
    return (
      this._sellingPlanGroup &&
      typeof this._sellingPlanGroup === "object" &&
      Object.keys(this._sellingPlanGroup).length > 0
    );
  }

  _isSellingPlanSelected() {
    const selectedPlan = document.querySelector('input[data-radio-type="selling_plan"]:checked');
    return !!selectedPlan;
  }

  _getCheckedRadioValue(name) {
    const radios = document.querySelectorAll(`input[name="${name}"]`);
    for (const radio of radios) {
      if (radio.checked) {
        return radio.value;
      }
    }
    return null;
  }

  _getSelectedBundleTotalSize() {
    const selectedInput = document.querySelector('input[name="bundle-size"]:checked');
    if (!selectedInput) return 0;

    return selectedInput.value || 0;
  }

  _renderSellingPlans(sellingPlanGroup) {
    if (!this._hasSellingPlans()) {
      return;
    }

    const container = document.querySelector("[data-selling-plans]");
    if (!container) return;
    container.innerHTML = "";

    const size = this._getSelectedBundleTotalSize();

    const { sortedPlans, planIds } = this._getSortedPlans(sellingPlanGroup);

    const sellingPlansContainer = this._createSellingPlansSection(sortedPlans, planIds, size);
    container.appendChild(sellingPlansContainer);

    if (this._isDoubleSubscriptionEnabled()) {
      container.appendChild(
        this._createDoubleSellingPlansSection(sortedPlans, planIds, size, this._getDoubleSubscriptionSize()),
      );
    }

    const oneTimeContainer = this._createOneTimeSection(size);
    container.appendChild(oneTimeContainer);

    this._attachPurchaseOptionHandlers();
    this._updateSavingsPercentInBenefitsList(size);
  }

  _getSortedPlans(sellingPlanGroup) {
    const sortedPlans = Object.values(sellingPlanGroup).sort((a, b) => a.position - b.position);

    const planIdsOrdered = Object.keys(sellingPlanGroup).sort(
      (a, b) => sellingPlanGroup[a].position - sellingPlanGroup[b].position,
    );

    const planIds = planIdsOrdered.map((id) => id.split("/").pop());

    return { sortedPlans, planIds };
  }

  _createSellingPlansSection(plans, planIds, size) {
    const container = document.createElement("div");
    container.classList.add(
      "skio-bundle-builder__purchase-option",
      "skio-bundle-builder__purchase-option--selling-plans",
    );

    const title = this._createSellingPlansTitle(window.subscriptionBoxTitlePretext);
    container.appendChild(title);

    const subtitleContainer = this._createSellingPlansSubtitle(size);
    container.appendChild(subtitleContainer);

    const dropdown = this._createPlansDropdown(plans, planIds, size);
    container.appendChild(dropdown);

    if (window.listItems && Array.isArray(window.listItems)) {
      const list = this._createBenefitsList();
      container.appendChild(list);
    }

    return container;
  }

  _isDoubleSubscriptionEnabled() {
    return this._enableDoubleSubscription === true && this._hasSellingPlans() && !!this._getDoubleSubscriptionSize();
  }

  // `size` is what the customer picked; `doubleSize` is the larger sizeInterval this option buys.
  // Prices, per-meal figures and the savings badge are all just the normal maths at doubleSize.
  _createDoubleSellingPlansSection(plans, planIds, size, doubleSize) {
    const container = document.createElement("div");
    container.classList.add(
      "skio-bundle-builder__purchase-option",
      "skio-bundle-builder__purchase-option--selling-plans",
      "skio-bundle-builder__purchase-option--double-selling-plans",
    );

    const title = this._createSellingPlansTitle(this._doubleSubscriptionBoxTitlePretext);
    container.appendChild(title);

    const subtitleContainer = this._createSellingPlansSubtitle(doubleSize, true);
    container.appendChild(subtitleContainer);

    // Frequency choices stay keyed to the picked size so both blocks offer the same plans; only
    // the discount label inside them reflects the larger tier.
    const dropdown = this._createPlansDropdown(plans, planIds, size, true, doubleSize);
    container.appendChild(dropdown);

    if (this._doubleSubscriptionText) {
      const text = this._createDoubleSubscriptionList(this._doubleSubscriptionText);
      if (text) container.appendChild(text);
    }

    return container;
  }

  // Each line (or semicolon-separated part, as the setting was originally written)
  // becomes a list item. Leading bullet characters are stripped so a merchant
  // pasting "- Free shipping" doesn't get a double bullet.
  _createDoubleSubscriptionList(rawText) {
    const items = rawText
      .split(/\r?\n|<br\s*\/?>|;/i)
      .map((line) => line.replace(/^\s*[-–—•*]\s*/, "").trim())
      .filter((line) => line.length > 0);

    if (items.length === 0) return null;

    const list = document.createElement("ul");
    list.classList.add("skio-bundle-builder__double-subscription-text");

    items.forEach((item) => {
      const li = document.createElement("li");
      li.innerHTML = item;
      list.appendChild(li);
    });

    return list;
  }

  _createSellingPlansTitle(pretext) {
    const title = document.createElement("div");
    title.classList.add("skio-bundle-builder__selling-plans-title");

    const content = document.createElement("div");
    content.classList.add("skio-bundle-builder__selling-plans-content");
    const pretextText = pretext || "Save";
    content.innerHTML = `${pretextText} <span data-total-percent></span>`;

    title.appendChild(content);
    return title;
  }

  _createSellingPlansSubtitle(size, isDouble = false) {
    const container = document.createElement("div");
    container.classList.add("skio-bundle-builder__purchase-subtitle-container");

    const topRowContainer = document.createElement("div");
    topRowContainer.classList.add("skio-bundle-builder__purchase-subtitle-top-row");

    const subscriptionTextTitle = window.subscriptionTextTitle || "Subscribe & Save";

    const subtitle = document.createElement("div");
    subtitle.classList.add("skio-bundle-builder__purchase-subtitle");
    subtitle.textContent = isDouble ? "Double Subscription" : subscriptionTextTitle;
    topRowContainer.appendChild(subtitle);

    container.appendChild(topRowContainer);

    const content = document.createElement("div");
    content.classList.add("skio-bundle-builder__purchase-content");
    container.appendChild(content);

    const priceBlock = document.createElement("span");
    priceBlock.classList.add(
      "skio-bundle-builder__price-block",
      isDouble ? "double-subscription-price" : "subscription-price",
    );

    this._renderPriceContainer(size, priceBlock, false, true, false, null, isDouble);

    const pricing = this._getBoxPricing(size, true);

    console.log("Box Pricing", pricing, size);

    const badgeBlock = document.createElement("span");
    badgeBlock.classList.add("skio-bundle-builder__badge-block");

    const mealPriceBlock = this._createMealPriceBlock(pricing.perMeal);
    if (mealPriceBlock) topRowContainer.appendChild(mealPriceBlock);

    /*  const saveBadge = this._createSaveBadge(size);
    badgeBlock.appendChild(saveBadge); */

    content.appendChild(priceBlock);
    content.appendChild(badgeBlock);

    return container;
  }

  _mealTypeSingular() {
    return this._mealType.endsWith("s") ? this._mealType.slice(0, -1) : this._mealType;
  }

  _createMealPriceBlock(perMeal) {
    if (!perMeal) return null;

    const costPerMeal = document.createElement("span");
    costPerMeal.classList.add("skio-bundle-builder__cost-per-meal");
    costPerMeal.textContent = `${this._formatMoney(perMeal)} `;

    const mealPriceSuffix = document.createElement("span");
    mealPriceSuffix.classList.add("skio-bundle-builder__meal-price-suffix");
    mealPriceSuffix.textContent = `per ${this._mealTypeSingular().toLowerCase()}`;

    const mealPriceText = document.createElement("span");
    mealPriceText.classList.add("skio-bundle-builder__meal-price");
    mealPriceText.appendChild(costPerMeal);
    mealPriceText.appendChild(mealPriceSuffix);

    return mealPriceText;
  }

  _createSaveBadge(size, addedCompareMultiplier = 1) {
    const badge = document.createElement("div");
    badge.classList.add("skio-bundle-builder__selling-plans-save", "box-badge");

    const { difference } = this._calculateSubscriptionDiscountSum(size, null, addedCompareMultiplier);
    badge.textContent = `${this._formatMoney(difference)} OFF`;

    return badge;
  }

  _createOneTimeSaveBadge(size) {
    const badge = document.createElement("div");
    badge.classList.add("skio-bundle-builder__one-time-save", "box-badge");

    const discountAmount = this._getOneTimeDiscountSum(size);
    badge.textContent = `${this._formatMoney(discountAmount)} OFF`;

    return badge;
  }

  _filterPlansBySize(plans, planIds, size, isDouble = false) {
    let filteredPlans = [...plans];
    let filteredPlanIds = [...planIds];

    // A small bundle normally drops the longest frequency, but a double order is twice the
    // product, so the double option keeps it (and defaults to it).
    if (size == 4 && !isDouble) {
      filteredPlans.pop();
      filteredPlanIds.pop();
    }

    if (size == 8 || size == 12) {
      filteredPlans.shift();
      filteredPlanIds.shift();
    }

    return {
      plans: filteredPlans,
      planIds: filteredPlanIds,
    };
  }

  // `size` drives which frequencies are offered; `pricingSize` drives the discount label, which
  // for the double option is the larger sizeInterval it buys.
  _createPlansDropdown(plans, planIds, size, isDouble = false, pricingSize = size) {
    const dropdown = document.createElement("div");
    dropdown.classList.add("custom-dropdown");

    const bundleDiscount = this._getMaxDiscountPercent(pricingSize, true);

    const icon = document.createElement("div");
    icon.classList.add("dropdown-icon");
    icon.innerHTML = this._svgDropdown;
    dropdown.appendChild(icon);

    const selected = document.createElement("div");
    selected.classList.add("selected");
    selected.textContent = "Select a plan";
    dropdown.appendChild(selected);

    const optionsContainer = document.createElement("div");
    optionsContainer.classList.add("options");
    const optionsList = document.createElement("div");
    optionsList.classList.add("options__list");
    optionsContainer.appendChild(optionsList);
    dropdown.appendChild(optionsContainer);

    const filtered = this._filterPlansBySize(plans, planIds, size, isDouble);

    this._populatePlansOptions(optionsList, filtered.plans, filtered.planIds, bundleDiscount, selected, isDouble);

    this._attachDropdownHandlers(selected, optionsContainer);

    return dropdown;
  }

  _populatePlansOptions(container, plans, planIds, bundleDiscount, selectedElement, isDouble = false) {
    const size = parseInt(this._getSelectedBundleTotalSize());

    plans.forEach((plan, idx) => {
      const label = document.createElement("label");
      const input = this._createPlanInput(plan, planIds[idx], isDouble);
      label.appendChild(input);

      const totalDiscount = plan.priceAdjustmentAmount + bundleDiscount;
      label.insertAdjacentHTML(
        "beforeend",
        `Delivery ${plan.title.toLowerCase()} <span class="visually-hidden"> -<span data-total-discount>${totalDiscount}</span>% OFF</span>`,
      );

      // The normal subscription option stays the default selection; the double one starts empty.
      if (!this._defaultOneTime && !isDouble) {
        let shouldCheck = false;

        if (size == 4) {
          shouldCheck = plans.length > 1 ? idx === 1 : idx === 0;
        } else {
          shouldCheck = idx === plans.length - 1;
        }

        if (shouldCheck) {
          input.checked = true;
          this._updateSelectedText(label, selectedElement);
        }
      }

      container.appendChild(label);
    });
  }

  _createPlanInput(plan, planId, isDouble = false) {
    const input = document.createElement("input");
    input.classList.add("visually-hidden");
    input.type = "radio";
    input.name = "purchase-option";
    input.value = planId;
    // Double subscriptions keep the "selling_plan" type so every price/gift path treats them as a
    // subscription; only the quantity multiplier below differs.
    input.dataset.radioType = "selling_plan";
    input.dataset.priceAdjustmentAmount = plan.priceAdjustmentAmount;
    if (isDouble) input.dataset.doubleSubscription = "true";

    return input;
  }

  _isDoubleSubscriptionSelected() {
    const selected = document.querySelector('input[name="purchase-option"]:checked');
    return selected?.dataset.doubleSubscription === "true";
  }

  // The double option is the next size up in the box's sizeInterval. Boxes that use this feature
  // are configured so that size is a whole multiple of the one below it, which is what makes
  // "the same picks, more of each" come out exact. Defaults to the picked size, but interval cards
  // pass their own size so each can preview what it would cost as a double.
  _getDoubleSubscriptionSize(fromSize) {
    const currentSize = parseInt(fromSize ?? this._getSelectedBundleTotalSize());
    if (!currentSize || !Array.isArray(this._sizeInterval)) return null;

    const nextSize = this._sizeInterval
      .map((value) => parseInt(value))
      .filter((value) => Number.isFinite(value) && value > currentSize)
      .sort((a, b) => a - b)[0];

    if (!nextSize || nextSize % currentSize !== 0) return null;

    return nextSize;
  }

  _getPurchaseQuantityMultiplier() {
    if (!this._isDoubleSubscriptionSelected()) return 1;

    const currentSize = parseInt(this._getSelectedBundleTotalSize());
    const doubleSize = this._getDoubleSubscriptionSize();
    if (!currentSize || !doubleSize) return 1;

    return doubleSize / currentSize;
  }

  // The size whose percentDiscountTier applies to what is actually being bought. Picking the
  // double option moves the whole box onto the larger size's tier.
  _getEffectivePricingSize() {
    const currentSize = parseInt(this._getSelectedBundleTotalSize());
    if (!this._isDoubleSubscriptionSelected()) return currentSize;

    return this._getDoubleSubscriptionSize() || currentSize;
  }

  // Value of the subscription extras to add on top of a compare-at price. Doubling the order doubles
  // the free products, but free shipping is only ever given once because it still ships once.
  // The values are theme settings entered in the shop's base currency, so they are converted to the
  // shopper's currency the same way Shopify converts prices.
  _getGiftCompareValue(quantityMultiplier = 1) {
    const baseValue = this._productsAddedCompare * quantityMultiplier + this._freeShippingAddedCompare;
    return this._roundedNumber(baseValue * this._getCurrencyRate());
  }

  // Shopify's base-to-presentment conversion rate; 1 when the shopper is in the base currency.
  _getCurrencyRate() {
    const rate = parseFloat(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  _resetOtherPlanDropdowns(activeBlock) {
    document.querySelectorAll(".skio-bundle-builder__purchase-option--selling-plans").forEach((block) => {
      if (block === activeBlock) return;

      block
        .querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]')
        .forEach((radio) => (radio.checked = false));

      const selected = block.querySelector(".custom-dropdown .selected");
      if (selected) selected.textContent = "Select a plan";

      const optionsContainer = block.querySelector(".options");
      if (optionsContainer) optionsContainer.style.display = "none";
    });
  }

  _updateSelectedText(label, selectedElement) {
    const clone = label.cloneNode(true);
    const inputInClone = clone.querySelector("input");
    if (inputInClone) inputInClone.remove();
    selectedElement.innerHTML = clone.innerHTML.trim();
  }

  _attachDropdownHandlers(selected, optionsContainer) {
    const toggleOptions = () => {
      const isVisible = optionsContainer.style.display === "block";
      optionsContainer.style.display = isVisible ? "none" : "block";
    };

    selected.addEventListener("click", toggleOptions);
    this._listeners.push({ el: selected, type: "click", fn: toggleOptions });

    optionsContainer.querySelectorAll('input[type="radio"]').forEach((input) => {
      const onPlanSelect = () => {
        input.checked = true;
        const label = input.closest("label");
        if (!label) return;

        this._updateSelectedText(label, selected);
        optionsContainer.style.display = "none";

        // Picking a plan in one option block deselects every other block, so clear their labels.
        this._resetOtherPlanDropdowns(input.closest(".skio-bundle-builder__purchase-option"));

        this._updateIntervalPriceBlock(document.querySelector("[data-box-interval]"));
        this._updateMainPrice();
        this._updateDiscountBlockTop();
        this._updateBundleItemsForPurchaseOption();
      };

      input.addEventListener("change", onPlanSelect);
      this._listeners.push({ el: input, type: "change", fn: onPlanSelect });
    });
  }

  _createBenefitsList() {
    const ul = document.createElement("ul");
    ul.classList.add("skio-bundle-builder__subscription-list");

    window.listItems.forEach((item, index) => {
      const li = document.createElement("li");
      li.innerHTML = `<span>${item.trim()}</span>`;
      if (index === 1) li.dataset.savingsPercent = "true";
      ul.appendChild(li);
    });

    return ul;
  }

  _updateSavingsPercentInBenefitsList(size) {
    const container = document.querySelector("[data-box-interval]");
    if (!container) return;
    const intervals = JSON.parse(container.dataset.intervals);
    const intervalOpt = intervals.find((opt) => opt.size == size && !opt.linked_bundle_id);
    if (!intervalOpt || !intervalOpt.savings_percentage) return;

    const firstLi = document.querySelector(".skio-bundle-builder__subscription-list [data-savings-percent]");
    if (!firstLi) return;

    const span = firstLi.querySelector("span");
    if (!span) return;

    span.innerHTML = span.innerHTML.replace(/\d+%/, `${+intervalOpt.savings_percentage}%`);
  }

  _createOneTimeSection(size) {
    const container = document.createElement("div");
    container.classList.add("skio-bundle-builder__purchase-option", "skio-bundle-builder__purchase-option--one-time");

    const title = this._createSellingPlansTitle(window.oneTimeBoxTitlePretext);
    container.appendChild(title);

    const label = document.createElement("label");

    const input = this._createOneTimeInput();

    if (!this._hasSellingPlans()) {
      input.checked = true;
    }

    label.appendChild(input);

    const subtitleContainer = this._createOneTimeSubtitle(size);
    label.appendChild(subtitleContainer);

    if (window.oneTimePurchaseItem) {
      const text = document.createElement("div");
      text.classList.add("skio-bundle-builder__one-time-text");
      text.textContent = window.oneTimePurchaseItem;
      label.appendChild(text);
    }

    container.appendChild(label);
    return container;
  }

  _createOneTimeInput() {
    const input = document.createElement("input");
    input.classList.add("visually-hidden");
    input.type = "radio";
    input.name = "purchase-option";
    input.value = "one-time";
    input.dataset.radioType = "one_time_purchase";

    if (this._defaultOneTime) {
      input.checked = true;
    }

    return input;
  }

  _createOneTimeSubtitle(size) {
    const container = document.createElement("div");
    container.classList.add("skio-bundle-builder__purchase-subtitle-container");

    const topRowContainer = document.createElement("div");
    topRowContainer.classList.add("skio-bundle-builder__purchase-subtitle-top-row");

    const subtitle = document.createElement("div");
    subtitle.classList.add("skio-bundle-builder__purchase-subtitle");
    subtitle.textContent = "One Time Purchase";
    topRowContainer.appendChild(subtitle);
    container.appendChild(topRowContainer);

    const content = document.createElement("div");
    content.classList.add("skio-bundle-builder__purchase-content");
    container.appendChild(content);

    const priceBlock = document.createElement("span");
    priceBlock.classList.add("skio-bundle-builder__price-block", "one-time-price");
    this._renderPriceContainer(size, priceBlock, false, false, true);

    const pricing = this._getBoxPricing(size, false);

    const badgeBlock = document.createElement("span");
    badgeBlock.classList.add("skio-bundle-builder__badge-block");

    const mealPriceBlock = this._createMealPriceBlock(pricing.perMeal);
    if (mealPriceBlock) topRowContainer.appendChild(mealPriceBlock);

    /*     const saveBadge = this._createOneTimeSaveBadge(size);
    badgeBlock.appendChild(saveBadge); */

    content.appendChild(priceBlock);
    content.appendChild(badgeBlock);

    return container;
  }

  _attachPurchaseOptionHandlers() {
    const sellingPlansBlock = this._getSellingPlansBlock();
    const doubleSellingPlansBlock = this._getDoubleSellingPlansBlock();
    const oneTimeBlock = document.querySelector(".skio-bundle-builder__purchase-option--one-time");
    if (!sellingPlansBlock || !oneTimeBlock) return;
    const intervalBlock = document.querySelector("[data-box-interval]");

    const onSellingPlansClick = (e) => {
      if (e.target.closest(".custom-dropdown")) return;
      this._handleSellingPlansSelection(sellingPlansBlock, oneTimeBlock, intervalBlock);
      this._elements.paymentsBlock?.classList.add("hidden");
    };

    const onOneTimeClick = () => {
      this._handleOneTimeSelection(sellingPlansBlock, oneTimeBlock, intervalBlock);
      this._elements.paymentsBlock?.classList.remove("hidden");
    };

    this._addListener(sellingPlansBlock, "click", onSellingPlansClick);
    this._addListener(oneTimeBlock, "click", onOneTimeClick);

    if (doubleSellingPlansBlock) {
      const onDoubleSellingPlansClick = (e) => {
        if (e.target.closest(".custom-dropdown")) return;
        this._handleSellingPlansSelection(doubleSellingPlansBlock, oneTimeBlock, intervalBlock);
        this._elements.paymentsBlock?.classList.add("hidden");
      };

      this._addListener(doubleSellingPlansBlock, "click", onDoubleSellingPlansClick);
    }
  }

  _getSellingPlansBlock() {
    return document.querySelector(
      ".skio-bundle-builder__purchase-option--selling-plans:not(.skio-bundle-builder__purchase-option--double-selling-plans)",
    );
  }

  _getDoubleSellingPlansBlock() {
    return document.querySelector(".skio-bundle-builder__purchase-option--double-selling-plans");
  }

  _handleSellingPlansSelection(sellingPlansBlock, oneTimeBlock, intervalBlock) {
    const isAlreadySelected =
      sellingPlansBlock.classList.contains("active") ||
      [...sellingPlansBlock.querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]')].some(
        (r) => r.checked,
      );

    if (isAlreadySelected) return;

    const sellingPlanRadios = sellingPlansBlock.querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]');
    const size = parseInt(this._getSelectedBundleTotalSize());

    const isDouble = sellingPlansBlock.classList.contains("skio-bundle-builder__purchase-option--double-selling-plans");

    let defaultRadio;
    if (isDouble) {
      // The double plan always defaults to the longest frequency, whatever the bundle size.
      defaultRadio = sellingPlanRadios[1] || sellingPlanRadios[0];
    } else if (size === 4) {
      defaultRadio = sellingPlanRadios[1] || sellingPlanRadios[0];
    } else {
      defaultRadio = sellingPlanRadios[1] || sellingPlanRadios[0];
    }

    if (defaultRadio) {
      defaultRadio.checked = true;

      const selected = sellingPlansBlock.querySelector(".selected");
      this._updateSelectedLabel(defaultRadio, selected);
    }

    // Clear the sibling subscription block (normal vs double) so only one plan reads as chosen.
    this._resetOtherPlanDropdowns(sellingPlansBlock);

    // After the reset, so the interval cards price off the option that ended up selected.
    this._updateIntervalPriceBlock(intervalBlock);

    this._setRadioChecked(oneTimeBlock, false);
    this._updatePurchaseOption();
    this._updateMainPrice();
    this._updateDiscountBlockTop();
  }

  _handleOneTimeSelection(sellingPlansBlock, oneTimeBlock, intervalBlock) {
    this._setRadioChecked(oneTimeBlock, true);

    // Passing null clears every subscription block (normal and double).
    this._resetOtherPlanDropdowns(null);

    this._updateIntervalPriceBlock(intervalBlock);
    this._updatePurchaseOption();
    this._updateMainPrice();
    this._updateDiscountBlockTop();
  }

  _updatePurchaseOption() {
    this._updateBundleItemsForPurchaseOption();
  }

  _setRadioChecked(element, checked) {
    const radio = element?.querySelector('input[type="radio"]');
    if (radio) radio.checked = checked;
  }

  _updateSelectedLabel(firstRadio, selectedElement) {
    if (!firstRadio || !selectedElement) return;

    const label = firstRadio.closest("label");
    if (!label) return;

    const clone = label.cloneNode(true);
    clone.querySelector("input")?.remove();
    selectedElement.innerHTML = clone.innerHTML.trim();
  }

  _addListener(element, type, handler) {
    element.addEventListener(type, handler);
    this._listeners.push({ el: element, type, fn: handler });
  }

  _addSellingPlanPercent() {
    setTimeout(() => {
      // The double block earns the larger sizeInterval's tier, so each block works out its own
      // headline percentage from the size it actually buys.
      const sellingPlansBlocks = document.querySelectorAll(".skio-bundle-builder__purchase-option--selling-plans");
      const oneTimeBlock = document.querySelector(".skio-bundle-builder__purchase-option--one-time");

      const size = this._getSelectedBundleTotalSize();
      const doubleSize = this._getDoubleSubscriptionSize();

      sellingPlansBlocks.forEach((sellingPlansBlock) => {
        const percentContainer = sellingPlansBlock.querySelector("[data-total-percent]");
        if (!percentContainer) return;

        const isDoubleBlock = sellingPlansBlock.classList.contains(
          "skio-bundle-builder__purchase-option--double-selling-plans",
        );
        const blockSize = isDoubleBlock ? doubleSize || size : size;
        const addedCompareMultiplier = isDoubleBlock && size ? blockSize / size : 1;

        const { percent } = this._calculateSubscriptionDiscountSum(blockSize, null, addedCompareMultiplier);
        percentContainer.textContent = `${percent}%`;
      });

      if (oneTimeBlock) {
        const percentContainer = oneTimeBlock.querySelector("[data-total-percent]");
        if (percentContainer) {
          percentContainer.textContent = `${this._getMaxDiscountPercent(size)}%`;
        }
      }
    }, 50);
  }

  _updateIntervalPriceBlock(block) {
    if (!block) return;
    const isSubscription = this._isSellingPlanSelected();

    block.querySelectorAll(".skio-bundle-builder__interval-container").forEach((label) => {
      this._updateIntervalContainerPrice(label, isSubscription);
    });
  }

  // Prices one interval card. Intervals of this box price off this box's sections and their own
  // radio value; linked intervals price off the other box's sections at its configured size.
  _updateIntervalContainerPrice(label, isSubscription) {
    if (!label) return;

    const linkedCtx = this._getLinkedContext(label);
    if (label.dataset.linkedBundleId && !linkedCtx) return; // linked bundle not loaded yet

    const input = label.querySelector('input[name="bundle-size"]');
    const size = linkedCtx ? linkedCtx.size : parseFloat(input?.value);
    if (!Number.isFinite(size)) return;

    const ctx = linkedCtx ? { sections: linkedCtx.sections, mealQuantity: linkedCtx.mealQuantity } : null;

    // While double is selected every card previews its own doubled price, so switching size keeps
    // showing what you would actually pay. Cards with no larger sizeInterval, and linked cards
    // (which price off a different box entirely), are left alone.
    const doubleSize =
      !linkedCtx && this._isDoubleSubscriptionSelected() ? this._getDoubleSubscriptionSize(size) : null;
    const pricingSize = doubleSize || size;
    const addedCompareMultiplier = doubleSize ? doubleSize / size : 1;

    const intervalBadge = label.querySelector(".skio-bundle-builder__interval-badge");
    const intervalDiscountBlock = label.querySelector(".discount-block");
    const priceDefault = label.querySelector(".price-default:not(.product-price)");
    const priceCompare = label.querySelector(".price-compare:not(.product-compare-price)");

    const pricing = this._getBoxPricing(pricingSize, isSubscription, ctx, addedCompareMultiplier);
    const undiscounted = this._getUndiscountedBoxPricing(pricingSize, ctx);

    if (priceCompare) {
      const compareValue =
        undiscounted.finalPrice + (isSubscription ? this._getGiftCompareValue(addedCompareMultiplier) : 0);
      priceCompare.textContent = `${this._formatMoney(compareValue)}`;
    }

    if (priceDefault) {
      priceDefault.textContent = `${this._formatMoney(pricing.finalPrice)}`;
    }

    if (intervalBadge) {
      const badgeText = label.closest("[data-box-interval]")?.getAttribute("data-badge-text") || "";
      intervalBadge.textContent = `${this._formatMoney(pricing.perMeal)} ${badgeText}`;
    }

    if (intervalDiscountBlock) {
      const savingsAmount = isSubscription
        ? this._calculateSubscriptionDiscountSum(pricingSize, ctx, addedCompareMultiplier).difference
        : this._getOneTimeDiscountSum(pricingSize, ctx);

      intervalDiscountBlock.innerHTML =
        savingsAmount > 0 ? `<span>Save</span> ${this._formatMoney(savingsAmount)}` : "";
    }
  }

  _updateMainPrice() {
    const block = document.querySelector(".skio-bundle-builder__main-price");
    if (!block) return;

    const regularEl = block.querySelector(".price-regular");
    const compareEl = block.querySelector(".price-compare");
    const savingsEl = block.querySelector("[data-main-savings]");
    const savingsBlock = block.querySelector(".price-savings");

    const checkedInput = document.querySelector('input[name="bundle-size"]:checked');
    const size = checkedInput ? parseInt(checkedInput.value) : this._preselectedSize;

    const isSubscription = this._isSellingPlanSelected();
    // The double option buys the next sizeInterval up, so both the box price and the tier move.
    const pricingSize = this._getEffectivePricingSize() || size;
    const pricing = this._getBoxPricing(pricingSize, isSubscription, null, this._getPurchaseQuantityMultiplier());

    if (regularEl) regularEl.textContent = this._formatMoney(pricing.finalPrice);
    if (compareEl) compareEl.textContent = pricing.savings > 0 ? this._formatMoney(pricing.comparePrice) : "";
    if (savingsEl) savingsEl.textContent = pricing.savings > 0 ? `${this._formatMoney(pricing.savings)} Off` : "";

    if (savingsBlock) {
      pricing.savings > 0
        ? savingsBlock.classList.remove("custom-hidden")
        : savingsBlock.classList.add("custom-hidden");
    }
  }

  _updatePurchasePriceBlock(block, size) {
    if (!block) return;

    const doubleSize = this._getDoubleSubscriptionSize(size);

    const subscriptionPriceBlock = block.querySelector(".skio-bundle-builder__price-block.subscription-price");
    if (subscriptionPriceBlock) {
      this._renderPriceContainer(size, subscriptionPriceBlock, false, true);
    }

    const doubleSubscriptionPriceBlock = block.querySelector(
      ".skio-bundle-builder__price-block.double-subscription-price",
    );
    if (doubleSubscriptionPriceBlock && doubleSize) {
      this._renderPriceContainer(doubleSize, doubleSubscriptionPriceBlock, false, true, false, null, true);
    }

    const oneTimePriceBlock = block.querySelector(".skio-bundle-builder__price-block.one-time-price");
    if (oneTimePriceBlock) {
      this._renderPriceContainer(size, oneTimePriceBlock, false, false, true);
    }

    const totalDiscountBlocks = block.querySelectorAll("[data-total-discount]");
    if (totalDiscountBlocks.length) {
      totalDiscountBlocks.forEach((totalDiscountBlock) => {
        const input = totalDiscountBlock?.closest("label")?.querySelector("input");
        const priceAdjustmentAmount = input?.getAttribute("data-price-adjustment-amount");
        // These blocks cover both purchase types, and volume discounts only apply to subscriptions,
        // so each label is resolved from its own radio rather than sharing one figure.
        const bundleDiscount = this._getMaxDiscountPercent(size, input?.dataset.radioType === "selling_plan");
        const totalDiscountAmount = +priceAdjustmentAmount + bundleDiscount;
        totalDiscountBlock.textContent = totalDiscountAmount;
      });
    }

    // Scoped per option block so the double block's dropdown doesn't overwrite the normal one's label.
    block.querySelectorAll(".skio-bundle-builder__purchase-option--selling-plans").forEach((optionBlock) => {
      const checkedRadio = optionBlock.querySelector('input[type="radio"][data-radio-type="selling_plan"]:checked');
      if (!checkedRadio) return;

      const isDoubleBlock = optionBlock.classList.contains(
        "skio-bundle-builder__purchase-option--double-selling-plans",
      );
      const bundleDiscount = this._getMaxDiscountPercent(isDoubleBlock ? doubleSize || size : size, true);
      const priceAdjustmentAmount = +checkedRadio.dataset.priceAdjustmentAmount;
      const totalDiscountAmount = priceAdjustmentAmount + bundleDiscount;

      const selectedBlock = optionBlock.querySelector(".custom-dropdown .selected");
      if (selectedBlock) {
        let discountSpan = selectedBlock.querySelector("[data-total-discount]");
        if (!discountSpan) {
          discountSpan = document.createElement("span");
          discountSpan.dataset.totalDiscount = "";
          selectedBlock.appendChild(discountSpan);
        }
        discountSpan.textContent = totalDiscountAmount;
      }
    });
  }

  _updateSubscriptionSaveBlock(saveBlock, size) {
    if (!saveBlock) return;

    const { difference } = this._calculateSubscriptionDiscountSum(size);
    const saveBlockText = saveBlock.closest("[data-selling-plans]")?.getAttribute("data-badge-discount-text") || "";

    saveBlock.textContent = `${saveBlockText} ${this._formatMoney(difference)}`;
  }

  _setupPurchaseOptionListener() {
    const purchaseOptionInputs = document.querySelectorAll('input[name="purchase-option"]');

    purchaseOptionInputs.forEach((input) => {
      const onChange = () => {
        const block = document.querySelector("[data-box-interval]");
        this._updateIntervalPriceBlock(block);
        this._updateBundleItemsForPurchaseOption();
        this._updateDiscountBlockTop();
      };
      input.addEventListener("change", onChange);
      this._listeners.push({ el: input, type: "change", fn: onChange });
    });
  }

  _setupIntervalOptionListener() {
    const inputs = document.querySelectorAll('input[name="bundle-size"]');
    const saveBlock = document.querySelector(".skio-bundle-builder__selling-plans-save");

    if (inputs.length) {
      inputs.forEach((input) => {
        const onChange = () => {
          const size = parseInt(input.value);

          this._sections.forEach((section) => {
            const sectionQuantity = this._getSectionQuantity(section.index);
            if (size < sectionQuantity) {
              const diff = sectionQuantity - size;
              this._reduceSectionQuantityFromEnd(section.index, diff);
            }
          });

          this._syncAllCardsAfterSizeChange();

          const purchaseBlock = document.querySelector("[data-selling-plans]");
          if (purchaseBlock) {
            this._updatePurchasePriceBlock(purchaseBlock, size);
          }

          if (saveBlock) {
            this._updateSubscriptionSaveBlock(saveBlock, size);
          }

          this._addSellingPlanPercent();
          this._updateDiscountBlockTop();
          this._updateRemainingQuantityBlock();
          this._toggleCheckoutButton();
          this._updateBundleControls(size);
          this._updateUpsellButtonsState();
          this._updateMainPrice();

          const currentSelection = document.querySelector('input[name="purchase-option"]:checked');
          const wasOneTime = currentSelection?.dataset.radioType === "one_time_purchase";
          const wasDoubleSubscription = currentSelection?.dataset.doubleSubscription === "true";

          this._renderSellingPlans(this._sellingPlanGroup);

          if (wasDoubleSubscription) {
            // Re-rendering resets to the normal subscription, so re-pick the double option.
            const doubleBlock = this._getDoubleSellingPlansBlock();
            const oneTimeBlock = document.querySelector(".skio-bundle-builder__purchase-option--one-time");
            if (doubleBlock) {
              this._handleSellingPlansSelection(
                doubleBlock,
                oneTimeBlock,
                document.querySelector("[data-box-interval]"),
              );
            }
          }

          if (wasOneTime) {
            const oneTimeRadio = document.querySelector(
              'input[name="purchase-option"][data-radio-type="one_time_purchase"]',
            );
            if (oneTimeRadio) {
              oneTimeRadio.checked = true;

              document.querySelectorAll('input[data-radio-type="selling_plan"]').forEach((radio) => {
                radio.checked = false;
              });

              const selected = document.querySelector(".custom-dropdown .selected");
              if (selected) {
                selected.textContent = "Select a plan";
              }
            }
          }

          this._renderProgressBarBlocks();
          this._renderStickyProgressBarBlocks();
          this._updateSectionProgressBars();
          this._updateStickyRemainingQuantityBlock();
          this._updateSubtotalSumBlock();
          this._updateMediaBySize(true);

          this._updateBundleItemsForPurchaseOption();
          this._updatePaymentBlock();
        };

        input.addEventListener("change", onChange);
        this._listeners.push({ el: input, type: "change", fn: onChange });
      });
    }
  }

  _reduceSectionQuantityFromEnd(sectionIndex, delta) {
    if (delta <= 0) return;

    for (let i = this._bundleItems.length - 1; i >= 0 && delta > 0; i--) {
      const item = this._bundleItems[i];
      if (item.sectionIndex !== sectionIndex) continue;

      if (item.quantity <= delta) {
        delta -= item.quantity;
        item.quantity = 0;
      } else {
        item.quantity -= delta;
        delta = 0;
      }
    }

    this._bundleItems = this._bundleItems.filter((item) => item.quantity > 0);
  }

  _planIdFromGid(gid) {
    return String(gid || "")
      .split("/")
      .pop();
  }

  /**
   * The purchase-option radios are rendered from the first section's selling plan group only,
   * so the selected plan id will not exist in the other sections' groups. Map the selected plan
   * onto the equivalent plan of the given section, otherwise those items get added to cart as
   * one time purchases.
   */
  _resolveSectionPlanId(section, selectedSellingPlanId) {
    const group = section?.sellingPlanGroup;
    if (!group || !selectedSellingPlanId) return null;

    const entries = Object.entries(group);
    const direct = entries.find(([gid]) => this._planIdFromGid(gid) === selectedSellingPlanId);
    if (direct) return selectedSellingPlanId;

    const selectedPlan = Object.entries(this._sellingPlanGroup || {}).find(
      ([gid]) => this._planIdFromGid(gid) === selectedSellingPlanId,
    )?.[1];
    if (!selectedPlan) return null;

    const sameFrequency = entries.find(
      ([, plan]) => plan.interval === selectedPlan.interval && plan.intervalCount === selectedPlan.intervalCount,
    );
    if (sameFrequency) return this._planIdFromGid(sameFrequency[0]);

    const sameTitle = entries.find(
      ([, plan]) =>
        String(plan.title || "")
          .trim()
          .toLowerCase() ===
        String(selectedPlan.title || "")
          .trim()
          .toLowerCase(),
    );
    if (sameTitle) return this._planIdFromGid(sameTitle[0]);

    // No exact frequency equivalent in this section - fall back to the closest one so the
    // items still go in as a subscription.
    const closest = entries
      .filter(([, plan]) => plan.interval === selectedPlan.interval)
      .sort((a, b) => {
        const diff =
          Math.abs(a[1].intervalCount - selectedPlan.intervalCount) -
          Math.abs(b[1].intervalCount - selectedPlan.intervalCount);
        return diff !== 0 ? diff : a[1].intervalCount - b[1].intervalCount;
      })[0];

    if (closest) {
      console.warn(
        `No "${selectedPlan.title}" selling plan on box "${section.title || section.boxId}", using "${closest[1].title}"`,
      );
      return this._planIdFromGid(closest[0]);
    }

    return null;
  }

  _getCurrentPriceData(variantId, sectionIndex) {
    const numericVariantId = String(variantId);
    const section =
      sectionIndex != null ? this._getSectionByIndex(sectionIndex) : this._getSectionOfVariant(numericVariantId);

    const variant = section
      ? this._getAllVariants(section.products).find((v) => v.platformId?.split("/").pop() === numericVariantId)
      : null;

    if (!section || !this._hasSellingPlans()) {
      return {
        price: variant?.price ?? 0,
        planId: null,
        type: "one_time_purchase",
      };
    }

    const selectedPlanInput = document.querySelector('input[name="purchase-option"]:checked');

    if (selectedPlanInput) {
      if (selectedPlanInput.dataset.radioType === "selling_plan") {
        const sectionPlanId = this._resolveSectionPlanId(section, selectedPlanInput.value);
        const selectedPlanGid = Object.keys(section.sellingPlanGroup || {}).find(
          (planGid) => this._planIdFromGid(planGid) === sectionPlanId,
        );
        if (selectedPlanGid) {
          const planData = section.sellingPlanGroup[selectedPlanGid];
          const mappingEntry = Object.entries(planData.variantMapping || {}).find(
            ([gid, mapping]) =>
              this._planIdFromGid(gid) === numericVariantId && this._planIdFromGid(mapping.planId) === sectionPlanId,
          );

          if (mappingEntry) {
            return {
              price: mappingEntry[1].adjustedPrice,
              planId: sectionPlanId,
              type: "selling_plan",
            };
          }
        }
      } else if (selectedPlanInput.dataset.radioType === "one_time_purchase") {
        if (variant) {
          return {
            price: variant.price,
            planId: null,
            type: "one_time_purchase",
          };
        }
      }
    }

    return {
      price: variant?.price ?? 0,
      planId: null,
      type: "one_time_purchase",
    };
  }

  _getComparePriceData(variantId, sectionIndex) {
    const numericVariantId = String(variantId);
    const selectedPlanInput = document.querySelector('input[name="purchase-option"]:checked');

    const section =
      sectionIndex != null ? this._getSectionByIndex(sectionIndex) : this._getSectionOfVariant(numericVariantId);
    const variant = section
      ? this._getAllVariants(section.products).find((v) => v.platformId?.split("/").pop() === numericVariantId)
      : null;

    if (!variant) return { comparePrice: 0 };
    if (!this._hasSellingPlans()) {
      return {
        comparePrice: variant.compareAtPrice || 0,
      };
    }

    if (selectedPlanInput) {
      if (selectedPlanInput.dataset.radioType === "selling_plan") {
        const sectionPlanId = this._resolveSectionPlanId(section, selectedPlanInput.value);
        const selectedPlanGid = Object.keys(section.sellingPlanGroup || {}).find(
          (planGid) => this._planIdFromGid(planGid) === sectionPlanId,
        );
        if (selectedPlanGid) {
          const planData = section.sellingPlanGroup[selectedPlanGid];
          const mappingEntry = Object.entries(planData.variantMapping || {}).find(
            ([gid, mapping]) =>
              this._planIdFromGid(gid) === numericVariantId && this._planIdFromGid(mapping.planId) === sectionPlanId,
          );

          if (mappingEntry) {
            return { comparePrice: variant.price };
          }
        }
      } else if (selectedPlanInput.dataset.radioType === "one_time_purchase") {
        if (variant.compareAtPrice && variant.compareAtPrice > 0) {
          return { comparePrice: variant.compareAtPrice };
        }
      }
    }

    return { comparePrice: variant.compareAtPrice || 0 };
  }

  _sortVariantsByWeight(variants) {
    const extractWeightInGrams = (title) => {
      const match = title.match(/(\d+)\s*(kg|g)/i);
      if (!match) return 0;
      const value = parseFloat(match[1]);
      const unit = match[2].toLowerCase();
      return unit === "kg" ? value * 1000 : value;
    };

    return variants.sort(
      (a, b) => extractWeightInGrams(a.productVariantTitle) - extractWeightInGrams(b.productVariantTitle),
    );
  }

  async _fetchBundleImageByDefaultVariant(variantId) {
    const query = `
      query ($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            metafield(namespace: "custom", key: "bundle_builder_image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch("/api/2024-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "ae3098bf902396fd7f396049b17d60fb",
        },
        body: JSON.stringify({
          query,
          variables: { id: variantId },
        }),
      });

      const json = await response.json();

      return json?.data?.node?.metafield?.reference?.image || null;
    } catch (error) {
      console.error("Error fetching bundle image:", error);
      return null;
    }
  }

  async _fetchVariantIconImage(variantId) {
    const query = `
      query ($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            metafield(namespace: "custom", key: "variant_image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch("/api/2024-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "ae3098bf902396fd7f396049b17d60fb",
        },
        body: JSON.stringify({
          query,
          variables: { id: variantId },
        }),
      });

      const json = await response.json();
      return json?.data?.node?.metafield?.reference?.image || null;
    } catch (error) {
      console.error("Error fetching variant icon image:", error);
      return null;
    }
  }

  _cleanProductTitle(titleText) {
    if (!titleText) return "";

    if (titleText.includes("Variety Pack")) {
      titleText = titleText.replace("Shake", "").replace("Soup", "").replace("Oats", "");
      return titleText.split(" ").slice(1).join(" ").replaceAll("Variety Pack 10", "Variety 10 Sachets");
    }
    if (titleText.includes("Key Lime Pie")) {
      return "Key Lime Pie";
    }
    return titleText.split(" ").slice(2).join(" ").split(" - ")[0];
  }

  _variantOrderIndex(variant, orderIndex) {
    const platformNumeric = variant.platformId?.split("/").pop();
    const candidates = [orderIndex[platformNumeric], orderIndex[variant.id]].filter((n) => n != null);
    return candidates.length ? Math.min(...candidates) : Number.MAX_SAFE_INTEGER;
  }

  _makeVariantPseudoProduct(product, variant) {
    // A single variant wrapped as a pseudo-product with one entry in productVariants, so
    // _createProductCard renders it as an individual card (no variant dropdown).
    const variantTitle = variant.productVariantTitle;
    const isDefaultTitle = !variantTitle || variantTitle === "Default Title";
    const displayTitle = isDefaultTitle ? this._cleanProductTitle(product.productTitle) : variantTitle;

    return {
      productId: product.productId,
      productTitle: product.productTitle,
      productImageSrc: product.productImageSrc,
      productPlatformId: product.productPlatformId,
      productHandle: product.productHandle,
      displayTitle,
      productVariants: [variant],
    };
  }

  _buildSections(dynamicBoxes) {
    // One section per bundle (the selection unit: one size target, progress bar and pricing tier).
    // productVariantGrouping only sub-divides the cards inside a section into visual sub-headings.
    return dynamicBoxes.map((dynamicBox, index) => {
      const settings = dynamicBox.settings || {};
      const grouping = Array.isArray(settings.productVariantGrouping) ? settings.productVariantGrouping : [];
      const rawProducts = dynamicBox.selectableProductVariants || [];

      const base = {
        index,
        boxId: dynamicBox.boxId,
        title: dynamicBox.sectionTitle || `Section ${index + 1}`,
        optional: !!dynamicBox.optional,
        sellingPlanGroup: dynamicBox.sellingPlanGroup || {},
        percentDiscountTier: dynamicBox.percentDiscountTier || {},
        sizeInterval: dynamicBox.sizeInterval || [],
      };

      // Map each variant's Skio UUID -> { product, variant }; groups reference these UUIDs.
      const byUuid = {};
      rawProducts.forEach((product) => {
        (product.productVariants || []).forEach((variant) => {
          if (variant?.id) byUuid[variant.id] = { product, variant };
        });
      });

      // productVariantOrder may list either numeric platformIds or Skio UUIDs; index both.
      const orderIndex = {};
      (settings.productVariantOrder || []).forEach((key, i) => {
        orderIndex[String(key)] = i;
      });

      const sortByOrder = (products) =>
        products.sort(
          (a, b) =>
            this._variantOrderIndex(a.productVariants[0], orderIndex) -
            this._variantOrderIndex(b.productVariants[0], orderIndex),
        );

      // No grouping configured on this box: still split every variant into its own card, just
      // without sub-headings. Grouping is per-box Skio config, so a box that lacks it would
      // otherwise fall back to multi-variant products and render variant dropdowns.
      if (!grouping.length) {
        const products = Object.values(byUuid).map(({ product, variant }) =>
          this._makeVariantPseudoProduct(product, variant),
        );
        sortByOrder(products);
        return { ...base, products, groups: null };
      }

      const usedUuids = new Set();
      const groups = [];

      grouping.forEach((group) => {
        const products = (group.productVariantIds || [])
          .map((uuid) => byUuid[uuid])
          .filter(Boolean)
          .map(({ product, variant }) => {
            usedUuids.add(variant.id);
            return this._makeVariantPseudoProduct(product, variant);
          });

        if (!products.length) return;

        sortByOrder(products);
        groups.push({ title: group.title, products });
      });

      // Variants in the bundle not referenced by any group fall back to a trailing "Other" group.
      const ungrouped = Object.values(byUuid)
        .filter(({ variant }) => !usedUuids.has(variant.id))
        .map(({ product, variant }) => this._makeVariantPseudoProduct(product, variant));

      if (ungrouped.length) {
        sortByOrder(ungrouped);
        groups.push({ title: "Other", products: ungrouped });
      }

      // Flattened list feeds pricing/lookup helpers; groups drive the sub-heading rendering.
      return { ...base, products: groups.flatMap((g) => g.products), groups };
    });
  }

  _renderProductCards() {
    if (!this._elements?.boxContainer) return;
    const container = document.createElement("div");
    container.classList.add("skio-bundle-builder__product-container");

    const allVariants = [];
    const allCards = [];

    console.log("this._sections", this._sections);

    this._sections.forEach((section, index) => {
      const products = section.products;
      if (!products || products.length === 0) {
        console.warn("Skipping section because no products:", section.title);
        return;
      }

      const categoryBlock = document.createElement("div");
      categoryBlock.classList.add("skio-bundle-box-category-block");
      categoryBlock.dataset.category = section.title;
      categoryBlock.dataset.sectionIndex = section.index;
      categoryBlock.dataset.boxId = section.boxId;

      /*       const heading = document.createElement("h2");
      heading.classList.add("skio-bundle-builder__product-category-title", "poppins-heading");

      const headingText = document.createElement("span");
      headingText.textContent = section.title;
      heading.appendChild(headingText); */

      if (section.optional) {
        const optionalBadge = document.createElement("span");
        optionalBadge.classList.add("skio-bundle-builder__optional-badge", "box-badge");
        optionalBadge.textContent = "Optional";
        heading.appendChild(optionalBadge);
      }

      /* const sectionProgress = document.createElement("div");
      sectionProgress.classList.add(
        "skio-bundle-builder__progress-bar-container",
        "skio-bundle-builder__section-progress",
      );
      sectionProgress.dataset.sectionProgress = section.index;
      sectionProgress.innerHTML = `
        <div class="skio-bundle-builder__progress-bar-text"><span data-section-progress-text></span></div>
        <div class="skio-bundle-builder__progress-bar" data-section-progress-bar></div>
      `;

      categoryBlock.appendChild(heading);
      categoryBlock.appendChild(sectionProgress); */

      const renderCard = (variant, parent) => {
        const card = this._createProductCard(variant, section.index);
        parent.appendChild(card);
        allVariants.push(variant);
        allCards.push(card);
      };

      if (section.groups && section.groups.length) {
        // Visual sub-headings within the section; the section itself stays the selection unit.
        section.groups.forEach((group) => {
          const groupBlock = document.createElement("div");
          groupBlock.classList.add("skio-bundle-builder__product-category-block");
          groupBlock.dataset.group = group.title;

          const groupHeading = document.createElement("h3");
          groupHeading.classList.add("skio-bundle-builder__product-category-title", "poppins-heading");
          const groupHeadingText = document.createElement("span");
          groupHeadingText.textContent = group.title;
          groupHeading.appendChild(groupHeadingText);

          const productList = document.createElement("div");
          productList.classList.add("skio-bundle-builder__product-list");

          group.products.forEach((variant) => renderCard(variant, productList));

          groupBlock.appendChild(groupHeading);
          groupBlock.appendChild(productList);
          categoryBlock.appendChild(groupBlock);
        });
      } else {
        const productList = document.createElement("div");
        productList.classList.add("skio-bundle-builder__product-list");

        products.forEach((variant) => renderCard(variant, productList));

        categoryBlock.appendChild(productList);
      }

      container.appendChild(categoryBlock);

      if (index > 0) {
        const stepTextContainer = document.createElement("div");
        stepTextContainer.classList.add("skio-bundle-builder__step-text-container");
        stepTextContainer.innerHTML = `<span class="skio-bundle-builder__step-text">Step ${index + 2}:</span>`;
        const stepTextContent = document.createElement("span");
        stepTextContent.textContent = this._elements.container?.dataset[`productSectionText-${index + 1}`];

        stepTextContainer.appendChild(stepTextContent);
        this._elements.container?.dataset[`productSectionText-${index + 1}`];
        categoryBlock.before(stepTextContainer);
      }
    });

    this._elements.boxContainer.appendChild(container);

    if (allVariants.length > 0) {
      this._applyProductTagsToAllCards(allVariants, allCards);
    }
  }

  _createProductCard(variant, sectionIndex) {
    const card = document.createElement("div");
    card.classList.add("product-card");
    card._variantData = variant;
    card.dataset.sectionIndex = sectionIndex;

    variant.productVariants = this._sortVariantsByWeight(variant.productVariants);

    const cardVariantId = variant.productVariants[0].platformId.split("/").pop();

    card.dataset.activeVariantId = cardVariantId;

    let selectedVariant = variant.productVariants[0];
    card.selectedVariant = selectedVariant;

    const cardContent = document.createElement("div");
    cardContent.classList.add("product-card__content");

    const title = document.createElement("h3");
    title.classList.add("skio-bundle-builder__title-heading", "poppins-heading");

    const titleText = variant.displayTitle || this._cleanProductTitle(variant.productTitle);
    title.innerHTML = `<span>${titleText}</span>`;

    const cardBottom = document.createElement("div");
    cardBottom.classList.add("product-card__bottom");

    const image = document.createElement("img");
    image.alt = "";
    card._bundleImageElement = image;

    const variantContainer = document.createElement("div");
    variantContainer.classList.add("product-card__variant-container");

    if (variant.productVariants.length > 1) {
      card.selectedVariant = selectedVariant;

      const variantButton = document.createElement("button");
      variantButton.classList.add("product-card__variant-button");
      variantButton.innerHTML = selectedVariant.productVariantTitle;

      const svgWrapper = document.createElement("span");
      svgWrapper.classList.add("product-card__variant-icon");
      svgWrapper.innerHTML = this._svgDropdown;

      const dropdown = this._createVariantDropdown(variant, card, variantButton, cardBottom, image, sectionIndex);

      variantContainer.appendChild(variantButton);
      variantContainer.appendChild(svgWrapper);
      variantContainer.appendChild(dropdown);
    }

    const addButton = document.createElement("button");
    addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
    addButton.dataset.label = this._addToCartText;
    addButton.setAttribute("aria-label", this._addToCartText);
    addButton.innerHTML = this._addToCartText;
    addButton.dataset.inCart = "false";

    const onAddButtonClick = (evt) => this._handleAddButtonClick(evt, card, variant, sectionIndex);
    addButton.addEventListener("click", onAddButtonClick);
    this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

    card.appendChild(image);
    card.appendChild(cardContent);
    cardContent.appendChild(title);
    cardContent.appendChild(variantContainer);
    cardContent.appendChild(cardBottom);
    cardBottom.appendChild(addButton);

    (async () => {
      const variantGID = variant.productVariants[0].platformId;
      let loaded = false;

      try {
        const bundleImage = await this._fetchBundleImageByDefaultVariant(variantGID);
        if (bundleImage?.url) {
          image.src = bundleImage.url;
          image.alt = bundleImage.altText || selectedVariant.productTitle;
          loaded = true;
        }
      } catch (err) {
        console.error("Error fetching bundle image", err);
      }

      if (!loaded) {
        image.src = selectedVariant.imageSrc || variant.productImageSrc;
        image.alt = selectedVariant.productTitle;
      }
    })();

    return card;
  }

  _createVariantDropdown(variant, card, variantButton, cardBottom, image, sectionIndex) {
    const dropdown = document.createElement("ul");
    dropdown.classList.add("product-card__dropdown");
    dropdown.style.display = "none";

    const onButtonClick = () => {
      document.querySelectorAll(".product-card__dropdown").forEach((drop) => {
        if (drop !== dropdown) drop.style.display = "none";
      });
      dropdown.style.display = dropdown.style.display === "none" ? "block" : "none";
    };
    variantButton.addEventListener("click", onButtonClick);
    this._listeners.push({ el: variantButton, type: "click", fn: onButtonClick });

    variant.productVariants.forEach((v) => {
      const item = document.createElement("li");
      item.textContent = v.productVariantTitle;

      const onItemClick = () => {
        card.selectedVariant = v;
        const variantId = v.platformId.split("/").pop();
        card.dataset.activeVariantId = variantId;
        variantButton.innerHTML = v.productVariantTitle;
        image.src = v.imageSrc || variant.productImageSrc;

        const priceData = this._getCurrentPriceData(variantId, sectionIndex);
        const priceEl = card.querySelector(".product-card__price");
        if (priceEl) priceEl.textContent = `$${priceData.price.toFixed(2)}`;

        const compareData = this._getComparePriceData(variantId, sectionIndex);
        const priceCompare = card.querySelector(".product-card__price-compare");
        if (priceCompare) {
          priceCompare.innerHTML = "";
          if (compareData.comparePrice > 0) {
            const span = document.createElement("span");
            span.textContent = `$${compareData.comparePrice.toFixed(2)}`;
            priceCompare.appendChild(span);
          }
        }

        dropdown.style.display = "none";

        const oldSelector = cardBottom.querySelector(".quantity-selector");
        if (oldSelector) cardBottom.removeChild(oldSelector);
        const oldButton = cardBottom.querySelector(".product-card__button");
        if (oldButton) cardBottom.removeChild(oldButton);

        this._recreateAddButton(card, cardBottom, variant, sectionIndex);
      };

      item.addEventListener("click", onItemClick);
      this._listeners.push({ el: item, type: "click", fn: onItemClick });

      dropdown.appendChild(item);
    });

    return dropdown;
  }

  _handleAddButtonClick(evt, card, variant, sectionIndex) {
    evt.preventDefault();

    const selected = card.selectedVariant || variant.productVariants[0];

    const cardBottom = card.querySelector(".product-card__bottom");
    const existingSelector = cardBottom.querySelector(".quantity-selector");
    if (existingSelector) {
      cardBottom.removeChild(existingSelector);
    }

    const result = this._createBundleQuantitySelector(card, { productVariants: [selected] }, 1, sectionIndex);

    if (result && result.container instanceof HTMLElement) {
      const addButton = cardBottom.querySelector(".product-card__button");
      if (addButton && cardBottom.contains(addButton)) {
        cardBottom.replaceChild(result.container, addButton);
      } else {
        cardBottom.appendChild(result.container);
      }
    }
  }

  _createBundleQuantitySelector(card, variant, initialQuantity = 0, sectionIndex = null) {
    const selected = card.selectedVariant || variant.productVariants[0];
    const variantId = this._getVariantId(selected);
    const resolvedSectionIndex = sectionIndex != null ? sectionIndex : parseInt(card.dataset.sectionIndex);

    const cardBottom = card.querySelector(".product-card__bottom");

    const container = document.createElement("div");
    container.classList.add("quantity-selector");

    const minusBtn = document.createElement("button");
    minusBtn.classList.add("minus");
    minusBtn.type = "button";
    minusBtn.innerHTML = this._svgMinus;

    const display = document.createElement("span");
    display.classList.add("quantity-display");
    display.textContent = "0";

    const plusBtn = document.createElement("button");
    plusBtn.classList.add("plus");
    plusBtn.type = "button";
    plusBtn.innerHTML = this._svgPlus;

    container.append(minusBtn, display, plusBtn);

    const updateQty = (delta) => {
      this._addToBundle(selected, delta, resolvedSectionIndex);

      const item = this._bundleItems.find((i) => i.id === variantId);
      display.textContent = item ? item.quantity : "0";

      if (!item || item.quantity <= 0) {
        this._removeFromBundleById(variantId);

        if (cardBottom.contains(container)) {
          cardBottom.removeChild(container);
        }

        this._recreateAddButton(card, cardBottom, variant, resolvedSectionIndex);
      }
    };

    const onPlus = () => updateQty(1);
    const onMinus = () => updateQty(-1);

    plusBtn.addEventListener("click", onPlus);
    minusBtn.addEventListener("click", onMinus);

    this._listeners.push({ el: plusBtn, type: "click", fn: onPlus });
    this._listeners.push({ el: minusBtn, type: "click", fn: onMinus });

    if (initialQuantity > 0) {
      updateQty(initialQuantity);
    }

    return { container, display };
  }

  _recreateAddButton(card, cardBottom, variant, sectionIndex = null) {
    const oldButton = cardBottom.querySelector(".product-card__button");
    if (oldButton) {
      cardBottom.removeChild(oldButton);
    }

    const resolvedSectionIndex = sectionIndex != null ? sectionIndex : parseInt(card.dataset.sectionIndex);

    const addButton = document.createElement("button");
    addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
    addButton.dataset.label = this._addToCartText;
    addButton.setAttribute("aria-label", this._addToCartText);
    addButton.innerHTML = this._addToCartText;
    addButton.dataset.inCart = "false";

    const onAddButtonClick = (evt) =>
      this._handleAddButtonClick(evt, card, variant || card._variantData, resolvedSectionIndex);
    addButton.addEventListener("click", onAddButtonClick);
    this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

    cardBottom.appendChild(addButton);

    const size = parseInt(this._getSelectedBundleTotalSize());
    const sectionFull = this._getSectionQuantity(resolvedSectionIndex) >= size;
    addButton.disabled = sectionFull;
    addButton.classList.toggle("disabled", sectionFull);
  }

  _resetBundleSelector(card, variant, sectionIndex = null) {
    const cardBottom = card.querySelector(".product-card__bottom");

    const existingSelector = cardBottom.querySelector(".quantity-selector");
    if (existingSelector) {
      cardBottom.removeChild(existingSelector);
      const prevVariantId = existingSelector.dataset.variantId;
      if (prevVariantId) {
        this._bundleItems = this._bundleItems.filter((i) => i.id !== prevVariantId);
      }
    }

    const addButton = cardBottom.querySelector(".product-card__button");
    if (!addButton) {
      this._recreateAddButton(card, cardBottom, variant, sectionIndex);
    }
  }

  _isPrimaryCard(card, variantId) {
    const cards = Array.from(
      document.querySelectorAll(`.product-card[data-active-variant-id="${variantId}"]:not(.product-card--upsell)`),
    );

    return cards[0] === card;
  }

  _syncCardWithBundleState(card, variantId) {
    const bundleItem = this._bundleItems.find((i) => i.id === variantId);
    const cardBottom = card.querySelector(".product-card__bottom");
    const selector = cardBottom?.querySelector(".quantity-selector");

    if (bundleItem && bundleItem.quantity > 0 && this._isPrimaryCard(card, variantId)) {
      if (selector) {
        const display = selector.querySelector(".quantity-display");
        if (display) {
          display.textContent = bundleItem.quantity;
        }
      } else {
        const variant = card._variantData;
        const { container } = this._createBundleQuantitySelector(
          card,
          variant,
          bundleItem.quantity,
          parseInt(card.dataset.sectionIndex),
        );

        const addButton = cardBottom.querySelector(".product-card__button");
        if (addButton) {
          cardBottom.removeChild(addButton);
        }

        cardBottom.appendChild(container);
      }
    } else {
      if (selector) {
        cardBottom.removeChild(selector);

        const variant = card._variantData;
        this._recreateAddButton(card, cardBottom, variant, parseInt(card.dataset.sectionIndex));
      }
    }
  }

  _syncAllCardsAfterSizeChange() {
    const cards = document.querySelectorAll(".product-card:not(.product-card--upsell)");

    cards.forEach((card) => {
      const variantId = card.dataset.activeVariantId;
      if (variantId) {
        this._syncCardWithBundleState(card, variantId);
      }
    });
  }

  _getVariantId(selectedVariant) {
    return selectedVariant.platformId.split("/").pop();
  }

  _getVariantPrice(selectedVariant) {
    return selectedVariant.price;
  }

  _getProductByVariantId(variantId) {
    for (const section of this._sections) {
      const product = section.products.find((candidate) =>
        candidate.productVariants.some((variant) => {
          const variantNum = variant.platformId.split("/").pop();
          return variantNum === String(variantId);
        }),
      );
      if (product) return product;
    }
    return null;
  }

  _addToBundle(variant, qty = 1, sectionIndex = null) {
    const variantId = this._getVariantId(variant);
    const resolvedSectionIndex = sectionIndex != null ? sectionIndex : this._getSectionOfVariant(variantId)?.index ?? 0;
    const section = this._getSectionByIndex(resolvedSectionIndex);

    const sectionMax = parseInt(this._getSelectedBundleTotalSize());
    const sectionQuantity = this._getSectionQuantity(resolvedSectionIndex);

    if (qty > 0 && sectionQuantity + qty > sectionMax) {
      this._updateBundleControls(sectionMax);
      this._updateUpsellButtonsState();
      this._updateRemainingQuantityBlock();
      this._updateSubtotalSumBlock();
      this._toggleCheckoutButton();
      return;
    }

    const priceData = this._getCurrentPriceData(variantId, resolvedSectionIndex);

    const existing = this._bundleItems.find((i) => i.id === variantId);

    if (existing) {
      existing.quantity += qty;

      if (existing.quantity <= 0) {
        this._bundleItems = this._bundleItems.filter((i) => i.id !== variantId);
      } else {
        existing.price = priceData.price;
        existing.planId = priceData.planId;
        if (!existing.originalPrice) {
          existing.originalPrice = this._getVariantPrice(variant);
        }
      }
    } else if (qty > 0) {
      const product = this._getProductByVariantId(variantId);

      const newItem = {
        product,
        id: variantId,
        sectionIndex: resolvedSectionIndex,
        boxId: section?.boxId,
        quantity: qty,
        price: priceData.price,
        originalPrice: this._getVariantPrice(variant),
        planId: priceData.planId,
        icon: null,
      };

      this._bundleItems.push(newItem);

      const self = this;

      const variantGID = variant.platformId;
      this._fetchVariantIconImage(variantGID)
        .then((iconImage) => {
          const imageUrl = iconImage?.url || null;
          if (imageUrl) {
            newItem.icon = imageUrl;
            self._updateProgressBarBlocks();
            self._updateStickyProgressBarBlocks();
          }
        })
        .catch((err) => console.error("Error fetching custom.variant_image", err));
    }

    this._updateBundleControls(sectionMax);
    this._updateUpsellButtonsState();
    this._updateRemainingQuantityBlock();
    this._updateSubtotalSumBlock();
    this._toggleCheckoutButton();
  }

  _removeFromBundle(variant, qty = 1) {
    const variantId = this._getVariantId(variant);
    const existing = this._bundleItems.find((i) => i.id === variantId);
    if (!existing) return;
    existing.quantity -= qty;
    if (existing.quantity <= 0) this._bundleItems = this._bundleItems.filter((i) => i.id !== variantId);
  }

  _removeFromBundleById(variantId) {
    const allVariants = this._getAllSectionsVariants();
    const variant = allVariants.find((v) => v.platformId.split("/").pop() === String(variantId));

    if (variant) {
      const card = document.querySelector(
        `.product-card[data-active-variant-id="${variantId}"]:not(.product-card--upsell)`,
      );

      const existingItem = this._bundleItems.find((i) => i.id === variantId);
      const sectionIndex = existingItem?.sectionIndex ?? (card ? parseInt(card.dataset.sectionIndex) : null);
      if (existingItem) {
        this._removeFromBundle(variant, existingItem.quantity);
      }

      if (card) {
        this._resetBundleSelector(card, variant, sectionIndex);
      }

      this._updateSubtotalSumBlock();
      this._updateRemainingQuantityBlock();
      this._toggleCheckoutButton();
      this._updateBundleControls(parseInt(this._getSelectedBundleTotalSize()));
      this._updateUpsellButtonsState();
    }
  }

  _updateBundleControls(sectionMax) {
    const previousFullState = this._sectionFullState;
    const previousSectionMax = this._sectionFullStateSize;
    const fullState = {};
    let justFilledIndex = null;

    this._sections.forEach((section) => {
      const sectionFull = this._getSectionQuantity(section.index) >= sectionMax;
      fullState[section.index] = sectionFull;

      if (sectionFull && previousFullState && !previousFullState[section.index] && justFilledIndex === null) {
        justFilledIndex = section.index;
      }

      const categoryBlock = document.querySelector(
        `.skio-bundle-box-category-block[data-section-index="${section.index}"]`,
      );
      if (!categoryBlock) return;

      categoryBlock.querySelectorAll(".add-button").forEach((btn) => {
        btn.disabled = sectionFull;
        btn.classList.toggle("disabled", sectionFull);
      });

      categoryBlock.querySelectorAll(".quantity-selector .plus").forEach((plusBtn) => {
        plusBtn.disabled = sectionFull;
        plusBtn.classList.toggle("disabled", sectionFull);
      });
    });

    this._sectionFullState = fullState;
    this._sectionFullStateSize = sectionMax;

    this._updateSectionProgressBars();

    // Only auto-scroll when a section fills up from adding items, not when the
    // bundle size itself changed (a smaller size can fill sections retroactively).
    if (justFilledIndex !== null && previousSectionMax === sectionMax) {
      this._scrollToNextUnfilledSection(justFilledIndex, fullState);
    }
  }

  _scrollToNextUnfilledSection(filledSectionIndex, fullState) {
    const unfilled = this._sections.filter((section) => !fullState[section.index]);
    if (unfilled.length === 0) return;

    // Prefer the next section after the one just filled, otherwise wrap to the first gap.
    const next = unfilled.find((section) => section.index > filledSectionIndex) || unfilled[0];
    const categoryBlock = document.querySelector(`.skio-bundle-box-category-block[data-section-index="${next.index}"]`);
    if (!categoryBlock) return;

    window.requestAnimationFrame(() => {
      categoryBlock.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  _getBundleTotals() {
    let totalQuantity = 0;
    let totalOriginalPrice = 0;

    const selectedSizeInput = document.querySelector('input[name="bundle-size"]:checked');
    const selectedSize = selectedSizeInput ? parseInt(selectedSizeInput.value) : 1;

    this._bundleItems.forEach((item) => {
      const quantity = parseInt(item.quantity || 1);
      totalQuantity += quantity;

      const original = item.originalPrice != null ? item.originalPrice : item.price;
      totalOriginalPrice += original * quantity;
    });

    let totalPrice = 0;

    // The double option moves the box onto the larger sizeInterval's tier.
    const pricingSize = this._getEffectivePricingSize() || selectedSize;

    this._bundleItems.forEach((item) => {
      const quantity = parseInt(item.quantity || 1);
      const section = this._getSectionByIndex(item.sectionIndex);
      const percent = section ? this._getDiscountPercent(section, pricingSize, this._isSellingPlanSelected()) : 0;

      const unitPrice = this._roundMoney(item.price);
      const rawUnitDiscount = unitPrice * (percent / 100);
      const unitDiscount = this._floorMoney(rawUnitDiscount);
      const finalUnitPrice = unitPrice - unitDiscount;

      totalPrice += finalUnitPrice * quantity;
    });

    // A double subscription buys more of each pick. totalQuantity stays as-is because it drives the
    // "how full is the box" progress/limit logic, but every money figure scales with the multiplier.
    const quantityMultiplier = this._getPurchaseQuantityMultiplier();
    totalPrice *= quantityMultiplier;
    totalOriginalPrice *= quantityMultiplier;

    const totalSavings = Math.max(0, totalOriginalPrice - totalPrice);

    return { totalQuantity, totalPrice, totalOriginalPrice, totalSavings };
  }

  _getTargetQuantity() {
    const size = parseInt(this._getSelectedBundleTotalSize()) || 0;

    return this._sections.reduce((target, section) => {
      if (!section.optional) return target + size;
      return target + (this._getSectionQuantity(section.index) > 0 ? size : 0);
    }, 0);
  }

  _getBundleSelectionStatus() {
    const maxQuantity = this._getTargetQuantity();
    const { totalQuantity } = this._getBundleTotals();

    const selectedQuantity = totalQuantity;
    const remainingQuantity = Math.max(maxQuantity - totalQuantity, 0);

    return { selectedQuantity, remainingQuantity, maxQuantity };
  }

  _isBundleReady() {
    const size = parseInt(this._getSelectedBundleTotalSize()) || 0;
    if (!size) return false;

    return this._sections.every((section) => {
      const quantity = this._getSectionQuantity(section.index);
      if (section.optional) {
        return quantity === 0 || quantity >= size;
      }
      return quantity >= size;
    });
  }

  _getUpsellTotals() {
    let upsellTotal = 0;

    this._upsellItems.forEach((item) => {
      const quantity = parseInt(item.quantity || 1);
      upsellTotal += item.price * quantity;
    });

    return upsellTotal;
  }

  _showTemporaryAlert({ duration = 3000, text } = {}) {
    if (this._elements.alertContainer) {
      this._elements.alertContainer.innerHTML = text;
      this._elements.alertContainer.classList.remove("custom-hidden");

      clearTimeout(this._removeAlert);
      this._removeAlert = setTimeout(() => {
        this._elements.alertContainer.innerHTML = "";
        this._elements.alertContainer.classList.add("custom-hidden");
      }, duration);
    }
  }

  _renderProgressBarBlocks() {
    const progressBar = this._elements.progressBar;
    if (!progressBar) return;

    const maxQuantity = this._getTargetQuantity();

    progressBar.innerHTML = "";

    for (let i = 0; i < maxQuantity; i++) {
      const block = document.createElement("div");
      block.classList.add("progress-block");
      block.dataset.index = i;
      progressBar.appendChild(block);
    }

    this._updateProgressBarBlocks();
  }

  _getProgressIcons() {
    const icons = [];
    const sortedItems = [...this._bundleItems].sort((a, b) => a.sectionIndex - b.sectionIndex);
    sortedItems.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        icons.push(item.icon || null);
      }
    });
    return icons;
  }

  _updateProgressBarBlocks() {
    const progressBar = this._elements.progressBar;
    const progressBarText = this._elements.progressBarText;

    if (!progressBar || !progressBarText) return;

    const maxQuantity = this._getTargetQuantity();

    if (progressBar.querySelectorAll(".progress-block").length !== maxQuantity) {
      progressBar.innerHTML = "";
      for (let i = 0; i < maxQuantity; i++) {
        const block = document.createElement("div");
        block.classList.add("progress-block");
        block.dataset.index = i;
        progressBar.appendChild(block);
      }
    }

    progressBarText.innerHTML = "";

    const { remainingQuantity } = this._getBundleSelectionStatus();
    const usedQuantity = maxQuantity - remainingQuantity;

    if (remainingQuantity === 0) {
      progressBarText.innerHTML = `<strong>Your bundle</strong> — ${maxQuantity} items`;
    } else {
      progressBarText.innerHTML = `Select <span data-progress-quantity>${remainingQuantity}</span> more items`;
    }

    const blocks = progressBar.querySelectorAll(".progress-block");
    const icons = this._getProgressIcons();

    blocks.forEach((block, index) => {
      if (index < usedQuantity) {
        block.classList.add("filled");

        const icon = icons[index];
        if (icon) {
          block.style.backgroundImage = `url(${icon})`;
          block.style.backgroundSize = "cover";
          block.style.backgroundPosition = "center";
        } else {
          block.style.backgroundImage = "";
        }
      } else {
        block.classList.remove("filled");
        block.style.backgroundImage = "";
      }
    });

    this._updateSectionProgressBars();
  }

  _updateSectionProgressBars() {
    const size = parseInt(this._getSelectedBundleTotalSize()) || 0;

    this._sections.forEach((section) => {
      const container = document.querySelector(`[data-section-progress="${section.index}"]`);
      if (!container) return;

      const textEl = container.querySelector("[data-section-progress-text]");
      const barEl = container.querySelector("[data-section-progress-bar]");
      if (!barEl) return;

      const quantity = this._getSectionQuantity(section.index);
      const remaining = Math.max(size - quantity, 0);

      if (barEl.querySelectorAll(".progress-block").length !== size) {
        barEl.innerHTML = "";
        for (let i = 0; i < size; i++) {
          const block = document.createElement("div");
          block.classList.add("progress-block");
          block.dataset.index = i;
          barEl.appendChild(block);
        }
      }

      const icons = [];
      this._bundleItems
        .filter((item) => item.sectionIndex === section.index)
        .forEach((item) => {
          for (let i = 0; i < item.quantity; i++) {
            icons.push(item.icon || null);
          }
        });

      barEl.querySelectorAll(".progress-block").forEach((block, index) => {
        if (index < quantity) {
          block.classList.add("filled");
          const icon = icons[index];
          if (icon) {
            block.style.backgroundImage = `url(${icon})`;
            block.style.backgroundSize = "cover";
            block.style.backgroundPosition = "center";
          } else {
            block.style.backgroundImage = "";
          }
        } else {
          block.classList.remove("filled");
          block.style.backgroundImage = "";
        }
      });

      if (textEl) {
        if (section.optional && quantity === 0) {
          textEl.innerHTML = `Optional — add ${size} ${section.title} or skip`;
        } else if (remaining === 0) {
          textEl.innerHTML = `<strong>${section.title}</strong> — ${quantity} of ${size} added`;
        } else {
          textEl.innerHTML = `Select <span>${remaining}</span> more ${section.title}`;
        }
      }
    });
  }

  _updateRemainingQuantityBlock() {
    if (!this._elements?.remainQuantityBlock || !this._elements?.remainQuantityContainer) return;

    const { remainingQuantity } = this._getBundleSelectionStatus();

    this._elements.remainQuantityBlock.textContent = `${remainingQuantity}`;

    if (remainingQuantity === 0) {
      this._elements.remainQuantityContainer.classList.add("custom-hidden");
    } else {
      this._elements.remainQuantityContainer.classList.remove("custom-hidden");
    }

    this._updateProgressBarBlocks();
    this._updateStickyUI();
  }

  _updateSubtotalSumBlock() {
    if (!this._elements.totalsContentBlock && !this._elements?.subtotalSumBlock) return;

    const { totalPrice, totalOriginalPrice, totalQuantity } = this._getBundleTotals();
    const upsellTotal = this._getUpsellTotals();
    const isSubscriptionSelected = this._isSellingPlanSelected();
    const compareTotalPrice =
      totalOriginalPrice +
      upsellTotal +
      (isSubscriptionSelected ? this._getGiftCompareValue(this._getPurchaseQuantityMultiplier()) : 0);
    const finalTotalPrice = totalPrice + upsellTotal;
    // totalPrice is already doubled for a double subscription, so the meal count has to be too.
    const mealTotal = totalQuantity * this._mealQuantity * this._getPurchaseQuantityMultiplier();
    const mealPrice = mealTotal > 0 ? this._formatMoney(totalPrice / mealTotal) : this._formatMoney(0);

    if (this._elements.subtotalCompareSumBlock) {
      this._elements.subtotalCompareSumBlock.textContent = `${this._formatMoney(compareTotalPrice)}`;
    }
    if (this._elements.subtotalSumBlock) {
      this._elements.subtotalSumBlock.textContent = `${this._formatMoney(finalTotalPrice)}`;
    }
    if (this._elements.subtotalMealPriceBlock) {
      this._elements.subtotalMealPriceBlock.textContent = `${mealPrice} per ${this._mealTypeSingular()}`;
    }

    const saveBlockWrapper = this._elements.totalsContentBlock;

    if (saveBlockWrapper) {
      if (this._isBundleReady() && totalQuantity > 0) {
        saveBlockWrapper.classList.remove("custom-hidden");
      } else {
        saveBlockWrapper.classList.add("custom-hidden");
      }
    }

    this._updatePaymentBlock();
  }

  _toggleCheckoutButton() {
    if (!this._elements?.checkoutButton || !this._elements?.checkoutBlock) return;

    const { totalQuantity } = this._getBundleTotals();
    const isReady = this._isBundleReady() && totalQuantity > 0;

    if (isReady) {
      const wasHidden = this._elements.checkoutButton.classList.contains("custom-hidden");
      this._elements.checkoutBlock.classList.remove("custom-hidden");
      this._elements.checkoutButton.classList.remove("custom-hidden");
      if (wasHidden) {
        const subscriptionFrequencySection = document.getElementById("skio-select-subscription-frequency");
        /* if (subscriptionFrequencySection) {
          subscriptionFrequencySection.scrollIntoView({ behavior: "smooth", block: "center" });
        } */
      }
    } else {
      this._elements.checkoutBlock.classList.add("custom-hidden");
      this._elements.checkoutButton.classList.add("custom-hidden");
    }
  }

  _updateBundleItemsForPurchaseOption() {
    this._bundleItems = this._bundleItems.map((item) => {
      const priceData = this._getCurrentPriceData(item.id, item.sectionIndex);
      return {
        ...item,
        price: priceData.price,
        planId: priceData.planId,
      };
    });

    const maxSize = parseInt(this._getSelectedBundleTotalSize());
    this._updateBundleControls(maxSize);
    this._updateUpsellButtonsState();
    this._updateRemainingQuantityBlock();
    this._updateSubtotalSumBlock();
    this._toggleCheckoutButton();
  }

  async _applyProductTagsToAllCards(variants, cards) {
    const root = this._elements?.container;
    const tagString = root?.dataset.tagString;
    if (!tagString) return;

    const targetTags = tagString
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    const uniqueHandles = [...new Set(variants.map((v) => v.productHandle))];

    const requests = uniqueHandles.map((handle) =>
      fetch(`/products/${handle}.js`)
        .then((res) => res.json())
        .then((data) => ({
          handle,
          tags: Array.isArray(data.tags) ? data.tags.map((t) => t.toLowerCase()) : [],
        }))
        .catch((err) => {
          console.error("Error fetching product tags", handle, err);
          return { handle, tags: [] };
        }),
    );

    const results = await Promise.all(requests);
    const tagsMap = new Map(results.map((r) => [r.handle, r.tags]));

    variants.forEach((variant, index) => {
      const productTags = tagsMap.get(variant.productHandle) || [];
      const hasMatch = targetTags.some((tag) => productTags.includes(tag));

      if (hasMatch && cards[index]) {
        cards[index].classList.add("product-limited");
      }
    });
  }

  _updateUpsellButtonsState() {
    document.querySelectorAll(".product-card--upsell").forEach((card) => {
      const button = card.querySelector(".add-button");
      if (!button) return;

      const isReady = this._isBundleReady();
      button.classList.toggle("is-disabled", !isReady);
    });

    document.querySelectorAll(".product-card--upsell .quantity-selector .plus").forEach((plusBtn) => {
      plusBtn.disabled = false;
      plusBtn.classList.remove("disabled");
    });

    document.querySelectorAll(".product-card--upsell .quantity-selector .minus").forEach((minusBtn) => {
      minusBtn.disabled = false;
      minusBtn.classList.remove("disabled");
    });
  }

  async _fetchUpsellProductImage(handle) {
    const query = `
      query ($handle: String!) {
        product(handle: $handle) {
          metafield(namespace: "custom", key: "single_flavour_product_image") {
            reference {
              ... on MediaImage {
                image {
                  url
                  altText
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch("/api/2024-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "ae3098bf902396fd7f396049b17d60fb",
        },
        body: JSON.stringify({
          query,
          variables: { handle },
        }),
      });

      const json = await response.json();

      return json?.data?.product?.metafield?.reference?.image || null;
    } catch (error) {
      console.error("Error fetching upsell product image:", error);
      return null;
    }
  }

  _renderProductUpsells() {
    if (!this._elements?.upsellsContainer) return;

    const upsellsVariants = this._upsellProducts;
    if (!upsellsVariants || upsellsVariants.length === 0) {
      return;
    }

    const upsellsProductList = document.createElement("div");
    upsellsProductList.classList.add("skio-bundle-builder__product-list");

    upsellsVariants.forEach((variant) => {
      const upsellsCard = this._createUpsellCard(variant);
      upsellsProductList.appendChild(upsellsCard);
    });

    this._elements.upsellsContainer.appendChild(upsellsProductList);
  }

  _createUpsellCard(variant) {
    const card = document.createElement("div");
    card.classList.add("product-card", "product-card--upsell");
    card._variantData = variant;
    card._isUpsell = true;

    variant.productVariants = this._sortVariantsByWeight(variant.productVariants);

    const cardVariantId = variant.productVariants[0].platformId.split("/").pop();
    card.dataset.activeVariantId = cardVariantId;

    let selectedVariant = variant.productVariants[0];
    card.selectedVariant = selectedVariant;

    const cardContent = document.createElement("div");
    cardContent.classList.add("product-card__content");

    const title = document.createElement("h3");
    title.classList.add("skio-bundle-builder__title-heading", "poppins-heading");
    let productTitle = variant.productTitle || "";
    productTitle = productTitle.startsWith("Dukes ") ? productTitle.replace(/^Dukes\s+/, "") : productTitle;
    title.innerHTML = `<span>${productTitle}</span>`;

    const cardBottom = document.createElement("div");
    cardBottom.classList.add("product-card__bottom");

    const priceContainer = document.createElement("div");
    priceContainer.classList.add("product-card__price-container");

    const price = document.createElement("p");
    price.classList.add("product-card__price");
    price.textContent = this._formatMoney(selectedVariant.price);

    priceContainer.appendChild(price);
    cardBottom.appendChild(priceContainer);

    const image = document.createElement("img");
    image.alt = "";
    card._upsellImageElement = image;

    const variantContainer = document.createElement("div");
    variantContainer.classList.add("product-card__variant-container");

    if (variant.productVariants.length > 1) {
      const variantButton = document.createElement("button");
      variantButton.classList.add("product-card__variant-button");
      variantButton.innerHTML = selectedVariant.productVariantTitle;

      const svgWrapper = document.createElement("span");
      svgWrapper.classList.add("product-card__variant-icon");
      svgWrapper.innerHTML = this._svgDropdown;

      const dropdown = this._createUpsellVariantDropdown(variant, card, variantButton, cardBottom, image);

      variantContainer.appendChild(variantButton);
      variantContainer.appendChild(svgWrapper);
      variantContainer.appendChild(dropdown);
    }

    const addButton = document.createElement("button");
    addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
    addButton.dataset.label = this._addToCartText;
    addButton.setAttribute("aria-label", this._addToCartText);
    addButton.innerHTML = this._addToCartText;
    addButton.dataset.inCart = "false";

    const onAddButtonClick = (evt) => this._handleUpsellAddButtonClick(evt, card, variant);
    addButton.addEventListener("click", onAddButtonClick);
    this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

    card.appendChild(image);
    card.appendChild(cardContent);
    cardContent.appendChild(title);
    cardContent.appendChild(variantContainer);
    cardContent.appendChild(cardBottom);
    cardBottom.appendChild(addButton);

    (async () => {
      const handle = variant.productHandle || variant.handle;
      let loaded = false;

      try {
        const upsellImage = await this._fetchUpsellProductImage(handle);
        if (upsellImage?.url) {
          image.src = upsellImage.url;
          image.alt = upsellImage.altText || selectedVariant.productTitle;
          loaded = true;
        }
      } catch (err) {
        console.error("Error fetching upsell metafield image", err);
      }

      if (!loaded) {
        image.src = selectedVariant.imageSrc || variant.productImageSrc;
        image.alt = selectedVariant.productTitle;
      }
    })();

    return card;
  }

  _handleUpsellAddButtonClick(evt, card, variant) {
    evt.preventDefault();

    const { totalQuantity } = this._getBundleTotals();

    if (totalQuantity === 0) {
      this._showTemporaryAlert({
        text: "Please add bundle products first",
        duration: 3000,
      });
      return;
    }

    if (!this._isBundleReady()) {
      this._showTemporaryAlert({
        text: "Please complete your bundle selection first",
        duration: 3000,
      });
      return;
    }

    const selected = card.selectedVariant || variant.productVariants[0];
    const cardBottom = card.querySelector(".product-card__bottom");
    const existingSelector = cardBottom.querySelector(".quantity-selector");

    if (existingSelector) {
      cardBottom.removeChild(existingSelector);
    }

    const result = this._createUpsellQuantitySelector(card, { productVariants: [selected] }, 1);

    if (result && result.container instanceof HTMLElement) {
      const addButton = cardBottom.querySelector(".product-card__button");
      if (addButton && cardBottom.contains(addButton)) {
        cardBottom.replaceChild(result.container, addButton);
      } else {
        cardBottom.appendChild(result.container);
      }
    }
  }

  _createUpsellQuantitySelector(card, variant, initialQuantity = 0) {
    const selected = card.selectedVariant || variant.productVariants[0];
    const variantId = this._getVariantId(selected);

    const container = document.createElement("div");
    container.classList.add("quantity-selector");
    container.dataset.upsellVariantId = variantId;

    const minusBtn = document.createElement("button");
    minusBtn.classList.add("minus");
    minusBtn.type = "button";
    minusBtn.innerHTML = this._svgMinus;

    const display = document.createElement("span");
    display.classList.add("quantity-display");
    display.textContent = "0";

    const plusBtn = document.createElement("button");
    plusBtn.classList.add("plus");
    plusBtn.type = "button";
    plusBtn.innerHTML = this._svgPlus;

    container.append(minusBtn, display, plusBtn);

    const updateQty = (delta) => {
      let currentQty = parseInt(display.textContent) || 0;
      let newQty = currentQty + delta;

      if (newQty < 0) newQty = 0;

      const existingUpsell = this._upsellItems.find((u) => u.id === variantId);

      if (newQty === 0) {
        this._upsellItems = this._upsellItems.filter((u) => u.id !== variantId);
        display.textContent = "0";

        const cardBottom = card.querySelector(".product-card__bottom");
        if (cardBottom.contains(container)) {
          cardBottom.removeChild(container);
        }

        const addButton = card.querySelector(".product-card__button");
        if (!addButton) {
          const newAddButton = document.createElement("button");
          newAddButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
          newAddButton.dataset.label = this._addToCartText;
          newAddButton.setAttribute("aria-label", this._addToCartText);
          newAddButton.innerHTML = this._addToCartText;

          const isReady = this._isBundleReady();
          newAddButton.classList.toggle("is-disabled", !isReady);

          const onAddButtonClick = (evt) => this._handleUpsellAddButtonClick(evt, card, variant);

          newAddButton.addEventListener("click", onAddButtonClick);
          this._listeners.push({ el: newAddButton, type: "click", fn: onAddButtonClick });

          cardBottom.appendChild(newAddButton);
        }
      } else {
        if (existingUpsell) {
          existingUpsell.quantity = newQty;
        } else {
          this._upsellItems.push({
            id: variantId,
            quantity: newQty,
            price: selected.price,
          });
        }
        display.textContent = newQty;
      }

      this._updateSubtotalSumBlock();
      this._updateStickySubtotalSumBlock();
    };

    const onPlus = () => updateQty(1);
    const onMinus = () => updateQty(-1);

    plusBtn.addEventListener("click", onPlus);
    minusBtn.addEventListener("click", onMinus);

    this._listeners.push({ el: plusBtn, type: "click", fn: onPlus });
    this._listeners.push({ el: minusBtn, type: "click", fn: onMinus });

    if (initialQuantity > 0) {
      updateQty(initialQuantity);
    }

    return { container, display };
  }

  _createUpsellVariantDropdown(variant, card, variantButton, cardBottom, image) {
    const dropdown = document.createElement("ul");
    dropdown.classList.add("product-card__dropdown");
    dropdown.style.display = "none";

    const onButtonClick = () => {
      document.querySelectorAll(".product-card__dropdown").forEach((drop) => {
        if (drop !== dropdown) drop.style.display = "none";
      });
      dropdown.style.display = dropdown.style.display === "none" ? "block" : "none";
    };
    variantButton.addEventListener("click", onButtonClick);
    this._listeners.push({ el: variantButton, type: "click", fn: onButtonClick });

    variant.productVariants.forEach((v) => {
      const item = document.createElement("li");
      item.textContent = v.productVariantTitle;

      const onItemClick = () => {
        card.selectedVariant = v;
        const variantId = v.platformId.split("/").pop();
        card.dataset.activeVariantId = variantId;
        variantButton.innerHTML = v.productVariantTitle;
        image.src = v.imageSrc || variant.productImageSrc;

        const priceEl = card.querySelector(".product-card__price");
        if (priceEl) priceEl.textContent = this._formatMoney(v.price);

        dropdown.style.display = "none";

        const oldSelector = cardBottom.querySelector(".quantity-selector");
        if (oldSelector) cardBottom.removeChild(oldSelector);
        const oldButton = cardBottom.querySelector(".product-card__button");
        if (oldButton) cardBottom.removeChild(oldButton);

        const addButton = document.createElement("button");
        addButton.classList.add("product-card__button", "button", "button--destructive");
        addButton.dataset.label = this._addToCartText;
        addButton.setAttribute("aria-label", this._addToCartText);
        addButton.innerHTML = this._addToCartText;
        cardBottom.appendChild(addButton);

        addButton.onclick = () => {
          const selected = card.selectedVariant || v;
          const { container } = this._createUpsellQuantitySelector(card, { productVariants: [selected] }, 1);
          container.dataset.upsellVariantId = this._getVariantId(selected);

          if (cardBottom.contains(addButton)) {
            cardBottom.replaceChild(container, addButton);
          } else {
            cardBottom.appendChild(container);
          }
        };
      };

      item.addEventListener("click", onItemClick);
      this._listeners.push({ el: item, type: "click", fn: onItemClick });

      dropdown.appendChild(item);
    });

    return dropdown;
  }

  _resetBundleUI() {
    document.querySelectorAll(".add-button").forEach((btn) => {
      btn.classList.remove("disabled");
      btn.disabled = false;
    });

    this._bundleItems = [];

    const allCards = document.querySelectorAll(".product-card:not(.product-card--upsell)");
    allCards.forEach((card) => {
      const variant = card._variantData;
      this._resetBundleSelector(card, variant, parseInt(card.dataset.sectionIndex));
    });

    this._updateSubtotalSumBlock();
    this._updateRemainingQuantityBlock();
    this._toggleCheckoutButton();
    this._updateSectionProgressBars();
  }

  _resetUpsellUI() {
    document.querySelectorAll(".product-card--upsell").forEach((card) => {
      const cardBottom = card.querySelector(".product-card__bottom");
      const selector = cardBottom?.querySelector(".quantity-selector");

      if (selector) {
        cardBottom.removeChild(selector);
      }

      const existingButton = cardBottom?.querySelector(".add-button");
      if (existingButton) return;

      const variant = card._variantData;
      const addButton = document.createElement("button");
      addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
      addButton.dataset.label = this._addToCartText;
      addButton.setAttribute("aria-label", this._addToCartText);
      addButton.innerHTML = this._addToCartText;

      const isReady = this._isBundleReady();
      addButton.disabled = !isReady;
      addButton.classList.toggle("is-disabled", !isReady);

      const onAddButtonClick = (evt) => this._handleUpsellAddButtonClick(evt, card, variant);
      addButton.addEventListener("click", onAddButtonClick);
      this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

      cardBottom?.appendChild(addButton);
    });
  }

  _trackBundleAddToCart(items, boxIndex) {
    const size = this._getEffectivePricingSize() || this._getSelectedBundleTotalSize();
    const { totalPrice } = this._getBundleTotals();
    const selectedRadio = document.querySelector('input[name="purchase-option"]:checked');
    const isSubscription = selectedRadio?.dataset.radioType === "selling_plan";
    const isDoubleSubscription = this._isDoubleSubscriptionSelected();
    const quantityMultiplier = this._getPurchaseQuantityMultiplier();
    const sellingPlanId = isSubscription ? selectedRadio?.value : null;

    const allVariants = this._getAllSectionsVariants();
    const gaItems = items.map((item) => {
      const variant = allVariants.find((v) => v.platformId?.split("/").pop() === String(item.id));
      const section = this._getSectionByIndex(item.sectionIndex);
      return {
        item_id: String(item.id),
        item_name: item.product?.productTitle || String(item.id),
        item_variant: variant?.productVariantTitle || "",
        quantity: (parseInt(item.quantity) || 1) * quantityMultiplier,
        price: parseFloat((item.price || 0).toFixed(2)),
        item_category: "Bundle",
        item_category2: section?.title || "",
      };
    });

    // GA4
    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        currency: this._userCurrencyCode || this._boxCurrencyCode || "AUD",
        value: totalPrice,
        items: gaItems,
      });

      window.gtag("event", "bundle_add_to_cart", {
        currency: this._userCurrencyCode || this._boxCurrencyCode || "AUD",
        value: totalPrice,
        bundle_size: size,
        bundle_id: this._sectionedBoxId,
        bundle_index: boxIndex,
        bundle_type: "sectioned",
        purchase_type: isDoubleSubscription ? "double_subscription" : isSubscription ? "subscription" : "one_time",
        selling_plan_id: sellingPlanId,
        items: gaItems,
      });
    }

    // Intelligems
    if (window.ig) {
      window.ig("trackEvent", "bundle_add_to_cart", {
        value: totalPrice,
        bundle_size: size,
        bundle_id: this._sectionedBoxId,
        bundle_type: "sectioned",
        purchase_type: isDoubleSubscription ? "double_subscription" : isSubscription ? "subscription" : "one_time",
      });
    }
  }

  async _addBundleToCart() {
    if (!this._bundleItems.length) return;

    const checkoutBtn = document.querySelector(".skio-bundle-builder__checkout");
    const buttonText = checkoutBtn?.querySelector("span");

    const checkoutStickyBtn = document.querySelector(".skio-bundle-builder__sticky-checkout");
    const stickyButtonText = checkoutStickyBtn?.querySelector("span");

    [checkoutBtn, checkoutStickyBtn].forEach((btn) => {
      if (btn) btn.disabled = true;
    });
    if (buttonText) buttonText.textContent = "Adding...";
    if (stickyButtonText) stickyButtonText.textContent = "Adding...";

    let boxIndex = 0;
    try {
      const cartResponse = await fetch("/cart.js");
      const cartData = await cartResponse.json();
      const existingBoxIndexes = cartData.items
        .map((cartItem) => Number(cartItem.properties?._dynamicBoxIndex))
        .filter((index) => !isNaN(index));

      if (existingBoxIndexes.length > 0) {
        boxIndex = Math.max(...existingBoxIndexes) + 1;
      }
    } catch (err) {
      console.error("Error:", err);
    }

    const itemsToAdd = [...this._bundleItems];
    const upsellItems = this._upsellItems.map((item) => ({
      id: Number(item.id),
      quantity: Number(item.quantity),
      properties: {
        _dynamicBoxIndex: boxIndex,
        _is_upsell: "true",
      },
    }));

    // A double subscription buys the picks at the next sizeInterval up, so each line scales by that
    // size ratio and the box is tagged with the size it actually adds up to.
    const quantityMultiplier = this._getPurchaseQuantityMultiplier();
    const cartBundleSize = this._getEffectivePricingSize() || this._getSelectedBundleTotalSize();

    const allItems = [
      ...itemsToAdd.map((item) => {
        const body = {
          id: Number(item.id),
          quantity: Number(item.quantity) * quantityMultiplier,
          properties: {
            _dynamicBoxIndex: boxIndex,
            _dynamicBoxId: item.boxId,
            _dynamicBoxSectionId: this._sectionedBoxId,
            _bundle_size: String(cartBundleSize) + "_bags",
            // How many boxes' worth this is, and how many items make up one box, so the cart gift
            // watcher can re-derive the gift allowance as the customer edits quantities. A sectioned
            // box is size x sections, so the item count can't be read off _bundle_size alone.
            _gift_multiplier: String(quantityMultiplier),
            _gift_step: String(this._getTargetQuantity()),
            _is_bundle: "true",
          },
        };
        if (item.planId) body.selling_plan = Number(item.planId);
        return body;
      }),
      ...upsellItems,
    ];

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ items: allItems }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error:", errorText);
        throw new Error(errorText);
      }

      this._bundleItems = [];
      this._upsellItems = [];

      this._trackBundleAddToCart(itemsToAdd, boxIndex);

      const selectedPurchaseRadio = document.querySelector('input[name="purchase-option"]:checked');
      const isSubscriptionPurchase = selectedPurchaseRadio?.dataset.radioType === "selling_plan";

      window._bundleGiftAddInProgress = isSubscriptionPurchase && this._giftVariantIds.length > 0;

      const giftCartPromise = window._bundleGiftAddInProgress
        ? fetch("/cart.js")
            .then((r) => r.json())
            .catch((err) => {
              console.error("Error fetching cart for gift check:", err);
              return null;
            })
        : null;

      if (window._bundleGiftAddInProgress && giftCartPromise) {
        const cart = await giftCartPromise;
        await this._addGiftsToCart(boxIndex, cart);
      }

      window._bundleGiftAddInProgress = false;

      [checkoutBtn, checkoutStickyBtn].forEach((btn) => {
        if (btn) btn.disabled = false;
      });
      if (buttonText) buttonText.textContent = "Add to cart";
      if (stickyButtonText) stickyButtonText.textContent = "Add to cart";

      this._resetBundleUI();
      this._resetUpsellUI();

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        await cartDrawer.updateContentsAndOpen();
        cartDrawer.open();
        this._lockGiftItemsInDrawer();
      }

      setTimeout(() => this._lockGiftItemsInDrawer(), 300);
    } catch (err) {
      console.error("Error:", err);
      [checkoutBtn, checkoutStickyBtn].forEach((btn) => {
        if (btn) btn.disabled = false;
      });
      if (buttonText) buttonText.textContent = "Add to cart";
      if (stickyButtonText) stickyButtonText.textContent = "Add to cart";
    }
  }

  async _addGiftsToCart(boxIndex, cart = null) {
    if (!this._giftVariantIds.length) return;

    const existingGiftQuantities = new Map();
    try {
      if (!cart) {
        const cartRes = await fetch("/cart.js");
        cart = await cartRes.json();
      }

      cart.items
        .filter((i) => i.properties?._is_gift === "true" && Number(i.properties?._dynamicBoxIndex) === Number(boxIndex))
        .forEach((i) => {
          const variantId = String(i.variant_id);
          existingGiftQuantities.set(variantId, (existingGiftQuantities.get(variantId) || 0) + i.quantity);
        });
    } catch (err) {
      console.error("Error checking cart for existing gifts:", err);
    }

    // A double subscription is that many boxes' worth, so the free gifts scale with it.
    const giftQuantity = this._getPurchaseQuantityMultiplier();
    const giftBundleSize = this._getEffectivePricingSize() || this._getSelectedBundleTotalSize();

    const items = this._giftVariantIds
      .map((id) => {
        // /cart/add.js adds to whatever is already there, so only top up the shortfall.
        const missingQuantity = giftQuantity - (existingGiftQuantities.get(String(id)) || 0);
        if (missingQuantity <= 0) return null;

        return {
          id: Number(id),
          quantity: missingQuantity,
          properties: {
            _dynamicBoxIndex: boxIndex,
            _is_gift: "true",
            _bundle_size: String(giftBundleSize) + "_bags",
          },
        };
      })
      .filter(Boolean);

    if (!items.length) return;

    try {
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
    } catch (err) {
      console.error("Error adding gifts:", err);
    }
  }

  async _removeGiftsFromCart(boxIndex) {
    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      const updates = {};
      cart.items.forEach((item) => {
        const isSameBox = Number(item.properties?._dynamicBoxIndex) === boxIndex;
        const isGift = item.properties?._is_gift === "true";
        if (isSameBox && isGift) {
          updates[item.key] = 0;
        }
      });
      if (!Object.keys(updates).length) return;
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
    } catch (err) {
      console.error("Error removing gifts:", err);
    }
  }

  _initCartGiftWatcher() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      let lockScheduled = false;
      this._cartGiftObserver = new MutationObserver(() => {
        if (lockScheduled) return;
        lockScheduled = true;
        setTimeout(() => {
          this._lockGiftItemsInDrawer();
          lockScheduled = false;
        }, 100);
      });
      this._cartGiftObserver.observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  _lockGiftItemsInDrawer() {
    if (!this._giftVariantIds.length) return;

    const rows = document.querySelectorAll("tr.cart-item");

    rows.forEach((row) => {
      const input = row.querySelector("input.quantity__input");
      if (!input) return;

      const variantId = String(input.dataset.quantityVariantId || "");
      const isGift = this._giftVariantIds.some((id) => String(id) === variantId);

      if (!isGift) return;

      const priceEl = row.querySelector(".price--end");
      const isFree = priceEl?.textContent?.includes("$0.00") || priceEl?.textContent?.trim() === "$0.00";

      if (!isFree) return;

      row.classList.add("cart-item--gift");

      row.querySelectorAll(".quantity__button").forEach((btn) => {
        btn.style.pointerEvents = "none";
      });

      input.disabled = true;
      input.style.pointerEvents = "none";
    });
  }

  _setupCheckoutButtonListener() {
    const checkoutBtn = this._elements.checkoutButton;

    if (!checkoutBtn) return;

    const onCheckoutClick = async (e) => {
      e.preventDefault();
      await this._addBundleToCart();
    };
    checkoutBtn.addEventListener("click", onCheckoutClick);
    this._listeners.push({ el: checkoutBtn, type: "click", fn: onCheckoutClick });
  }

  _renderStickyProgressBarBlocks() {
    const progressBar = this._sticky?.progressBar;
    if (!progressBar) return;

    const maxQuantity = this._getTargetQuantity();
    progressBar.innerHTML = "";

    for (let i = 0; i < maxQuantity; i++) {
      const block = document.createElement("div");
      block.classList.add("progress-block");
      block.dataset.index = i;
      progressBar.appendChild(block);
    }

    this._updateStickyProgressBarBlocks();
  }

  _updateStickyProgressBarBlocks() {
    const progressBar = this._sticky?.progressBar;
    const progressBarText = this._sticky?.progressBarText;

    if (!progressBar || !progressBarText) return;

    const maxQuantity = this._getTargetQuantity();

    if (progressBar.querySelectorAll(".progress-block").length !== maxQuantity) {
      progressBar.innerHTML = "";
      for (let i = 0; i < maxQuantity; i++) {
        const block = document.createElement("div");
        block.classList.add("progress-block");
        block.dataset.index = i;
        progressBar.appendChild(block);
      }
    }

    progressBarText.innerHTML = "";

    const { remainingQuantity } = this._getBundleSelectionStatus();
    const usedQuantity = maxQuantity - remainingQuantity;

    if (remainingQuantity === 0) {
      progressBarText.innerHTML = `<strong>Your bundle</strong> — ${maxQuantity} items`;
    } else {
      progressBarText.innerHTML = `Select <span data-progress-quantity>${remainingQuantity}</span> more items`;
    }

    const blocks = progressBar.querySelectorAll(".progress-block");
    const icons = this._getProgressIcons();

    blocks.forEach((block, index) => {
      if (index < usedQuantity) {
        block.classList.add("filled");

        const icon = icons[index];
        if (icon) {
          block.style.backgroundImage = `url(${icon})`;
          block.style.backgroundSize = "cover";
          block.style.backgroundPosition = "center";
        } else {
          block.style.backgroundImage = "";
        }
      } else {
        block.classList.remove("filled");
        block.style.backgroundImage = "";
      }
    });

    this._toggleStickyCheckoutButton(remainingQuantity);
  }

  _toggleStickyCheckoutButton(remainingQuantity) {
    const button = this._sticky?.checkoutButton;
    const note = this._sticky?.noteBlock;
    if (!button && !note) return;

    const { totalQuantity } = this._getBundleTotals();
    const isReady = this._isBundleReady() && totalQuantity > 0;

    if (isReady && remainingQuantity === 0) {
      button?.classList.remove("custom-hidden");
      note?.classList.add("custom-hidden");
    } else {
      button?.classList.add("custom-hidden");
      note?.classList.remove("custom-hidden");
    }
  }

  _updateStickyRemainingQuantityBlock() {
    const el = this._sticky;
    if (!el?.container || !el?.quantity) return;

    const { remainingQuantity, maxQuantity } = this._getBundleSelectionStatus();

    const usedQuantity = maxQuantity - remainingQuantity;

    el.quantity.textContent = `${remainingQuantity}`;

    if (el.fill && maxQuantity > 0) {
      const progress = (usedQuantity / maxQuantity) * 100;
      el.fill.style.width = `${progress}%`;
    }
  }

  _bindStickyCheckoutButton() {
    const stickyBtn = this._sticky?.checkoutButton;
    const mainBtn = this._elements?.checkoutButton;

    if (!stickyBtn || !mainBtn) return;

    const onStickyBtnClick = (e) => {
      e.preventDefault();
      mainBtn.click();
    };

    stickyBtn.addEventListener("click", onStickyBtnClick);
    this._listeners.push({ el: stickyBtn, type: "click", fn: onStickyBtnClick });
  }

  _updateStickySubtotalSumBlock() {
    const el = this._sticky;
    if (!el?.totalsWrapper || !el?.subtotal || !el?.compare || !el?.meal) return;

    const { totalQuantity, totalPrice, totalOriginalPrice } = this._getBundleTotals();

    const upsellTotal = this._getUpsellTotals();
    const finalTotalPrice = totalPrice + upsellTotal;

    const compareTotalPrice =
      totalOriginalPrice +
      upsellTotal +
      (this._isSellingPlanSelected() ? this._getGiftCompareValue(this._getPurchaseQuantityMultiplier()) : 0);

    // totalPrice is already doubled for a double subscription, so the meal count has to be too.
    const mealTotal = totalQuantity * this._mealQuantity * this._getPurchaseQuantityMultiplier();

    if (mealTotal > 0) {
      const mealPrice = this._formatMoney(totalPrice / mealTotal);
      el.meal.innerHTML = `<span class="skio-bundle-builder__cost-per-meal">${mealPrice}</span> per ${this._mealTypeSingular().toLowerCase()}`;
    }

    el.compare.textContent = compareTotalPrice > finalTotalPrice ? this._formatMoney(compareTotalPrice) : "";

    el.subtotal.textContent = this._formatMoney(finalTotalPrice);

    el.totalsWrapper.classList.toggle("custom-hidden", !(this._isBundleReady() && totalQuantity > 0));
  }

  _updateStickyUI() {
    this._updateStickyRemainingQuantityBlock();
    this._updateStickyProgressBarBlocks();
    this._updateStickySubtotalSumBlock();
  }

  _updateMediaBySize(shouldScroll = false) {
    const selectedSize = String(this._getSelectedBundleTotalSize());

    const sizes = this._sizeInterval.map(String);

    const mediaItems = document.querySelectorAll(".product__media-item, .thumbnail-list__item");
    let firstVisibleMedia = null;

    mediaItems.forEach((el) => {
      const img = el.querySelector("img");
      if (!img) return;

      const alt = (img.getAttribute("alt") || "").toLowerCase();

      if (!alt.trim()) {
        el.style.display = "";
        if (!firstVisibleMedia) firstVisibleMedia = el;
        return;
      }

      const matchedSize = sizes.find((size) => alt.startsWith(size + "-"));

      if (!matchedSize || matchedSize === selectedSize) {
        el.style.display = "";
        if (!firstVisibleMedia) firstVisibleMedia = el;
      } else {
        el.style.display = "none";
      }
    });

    if (shouldScroll && firstVisibleMedia) {
      const container = document.querySelector("[data-box-interval]");
      if (!container) return;
      const intervals = JSON.parse(container.dataset.intervals);

      const intervalOpt = intervals.find((opt) => opt.size == selectedSize);
      if (!intervalOpt || !intervalOpt.product_image_interval) return;

      const slideItem = document
        .querySelector(`img[src="${intervalOpt.product_image_interval}"]`)
        ?.closest(".product__media-item");

      if (!slideItem) {
        return;
      }
      const imageContainer = document.querySelector(".product__media-list");
      if (!imageContainer) return;
      const containerLeft = imageContainer.getBoundingClientRect().left;
      const slideItemLeft = slideItem.getBoundingClientRect().left;
      const scrollTarget = imageContainer.scrollLeft + (slideItemLeft - containerLeft);
      imageContainer.scrollTo({ left: scrollTarget, behavior: "instant" });
    }
  }

  _updateDiscountBlockTop() {
    const discountSpan = document.querySelector(".discount-block-top  span");

    console.log("discountSpan", discountSpan);
    if (!discountSpan) return;

    const size = this._getEffectivePricingSize() || this._getSelectedBundleTotalSize();
    const isSubscription = this._isSellingPlanSelected();

    console.log("Got here");

    if (isSubscription) {
      const { difference } = this._calculateSubscriptionDiscountSum(size, null, this._getPurchaseQuantityMultiplier());
      if (!difference) return;
      discountSpan.textContent = this._formatMoney(difference);

      console.log("difference", difference);
    } else {
      const savings = this._getOneTimeDiscountSum(size);
      if (!savings) return;
      discountSpan.textContent = this._formatMoney(savings);
      console.log("savings", savings);
    }
  }

  _initStickyBarVisibilityObserver() {
    const totalsBlock = document.querySelector(".skio-bundle-builder__totals");
    const stickyBar = document.querySelector(".skio-bundle-builder__sticky-bar-container");

    if (!totalsBlock || !stickyBar) return;

    const OFFSET_FROM_BOTTOM = 120;
    const builder = stickyBar.closest(".skio-bundle-builder__sticky-container");

    // The bar is fixed to the viewport, so it follows the page for the whole
    // document rather than only while the builder is on screen. It steps aside
    // just for the stretch where the in-page totals block — which carries the
    // same checkout CTA — is on screen, and comes straight back afterwards.
    let hidden = null;
    const applyVisibility = () => {
      const totals = totalsBlock.getBoundingClientRect();
      const shouldHide = totals.bottom > 0 && totals.top <= window.innerHeight - OFFSET_FROM_BOTTOM;

      if (shouldHide === hidden) return;
      hidden = shouldHide;
      stickyBar.classList.toggle("is-hidden", shouldHide);
    };

    window.addEventListener("scroll", applyVisibility, { passive: true });
    window.addEventListener("resize", applyVisibility);

    if (builder) {
      // Reserve the out-of-flow bar's height at the foot of the builder.
      const syncStickyBarHeight = () => {
        builder.style.setProperty("--sticky-bar-height", `${stickyBar.offsetHeight}px`);
        applyVisibility();
      };
      syncStickyBarHeight();
      if (window.ResizeObserver) {
        new ResizeObserver(syncStickyBarHeight).observe(stickyBar);
        // Selecting products reflows the builder without any scroll event.
        new ResizeObserver(applyVisibility).observe(builder);
      }
    }

    applyVisibility();
  }

  _calculateCurrentTotalPrice() {
    const upsellTotal = this._getUpsellTotals();

    const checkedInput = document.querySelector('input[name="bundle-size"]:checked');
    const pickedSize = checkedInput ? parseInt(checkedInput.value) : this._preselectedSize;
    // The double option buys the next sizeInterval up, so both the box price and the tier move.
    const size = this._getEffectivePricingSize() || pickedSize;

    const isSubscription = this._isSellingPlanSelected();
    const pricing = this._getBoxPricing(size, isSubscription, null, this._getPurchaseQuantityMultiplier());

    return pricing.finalPrice + upsellTotal;
  }

  _updatePaymentBlock() {
    const el = document.querySelector("[data-payment]");
    if (!el) return;

    const total = this._calculateCurrentTotalPrice();
    const installment = total / 4;

    el.textContent = this._formatMoney(installment);
  }

  _initReviewsSlider() {
    const list = document.querySelector(".skio-bundle-builder__reviews-list");
    const dots = document.querySelectorAll(".skio-bundle-builder__reviews-dot");
    if (!list || !dots.length) return;

    const slides = Array.from(list.querySelectorAll(".skio-bundle-builder__review-slide"));

    const getActiveIndex = () => {
      let closest = 0;
      let minDist = Infinity;
      slides.forEach((slide, i) => {
        const dist = Math.abs(slide.offsetLeft - list.scrollLeft);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      return closest;
    };

    const setActiveDot = (index) => {
      dots.forEach((dot, i) => dot.classList.toggle("active", i === index));
    };

    list.addEventListener("scroll", () => {
      setActiveDot(getActiveIndex());
    });

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const index = parseInt(dot.dataset.index, 10);
        list.scrollTo({ left: slides[index].offsetLeft, behavior: "smooth" });
      });
    });

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasDragged = false;

    list.addEventListener("mousedown", (e) => {
      isDragging = true;
      hasDragged = false;
      startX = e.pageX;
      startScrollLeft = list.scrollLeft;
      list.style.cursor = "grabbing";
      list.style.userSelect = "none";
      list.style.scrollBehavior = "auto";
      list.style.scrollSnapType = "none";
    });

    list.addEventListener("mousemove", (e) => {
      if (!isDragging) return;
      const delta = e.pageX - startX;
      if (Math.abs(delta) > 5) hasDragged = true;
      list.scrollLeft = startScrollLeft - delta;
    });

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      list.style.cursor = "";
      list.style.userSelect = "";
      list.style.scrollBehavior = "";
      list.style.scrollSnapType = "";
      if (hasDragged) {
        list.scrollTo({ left: slides[getActiveIndex()].offsetLeft, behavior: "smooth" });
      }
    };

    list.addEventListener("mouseup", endDrag);
    list.addEventListener("mouseleave", endDrag);
  }

  _removeAllListeners() {
    this._listeners.forEach(({ el, type, fn }) => {
      el.removeEventListener(type, fn);
    });
    this._listeners = [];

    if (this._cartGiftObserver) {
      this._cartGiftObserver.disconnect();
    }
  }
}

const sectionedBundleBlock = document.querySelector(".skio-bundle-builder__categories-wrapper");

let sectionedBundleInstance = null;

if (sectionedBundleBlock) {
  const sectionedBoxId = sectionedBundleBlock.dataset.sectionedBoxId;

  if (sectionedBoxId) {
    sectionedBundleInstance = new SectionedBundleBox(
      "https://api.skio.com/storefront-v2-http/get-sectioned-box",
      sectionedBoxId,
    );
  }
}

window.addEventListener("beforeunload", () => {
  if (sectionedBundleInstance) {
    sectionedBundleInstance._removeAllListeners();
    sectionedBundleInstance = null;
  }
});
